import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Room, RoomEvent, Track, createLocalVideoTrack, LocalAudioTrack,
} from "livekit-client";
import {
  Disc3, ArrowLeft, Play, Pause, Square, Upload, Coffee,
  ChevronLeft, ChevronRight, Copy, Check, AlertTriangle, Loader2,
  Crown, Users, Shield, UserMinus, UserPlus, UserCheck, Ban, RotateCcw, X, Clock,
  Sliders, LayoutGrid, Columns, LogOut, OctagonAlert, MoreVertical, PictureInPicture2, Smile, Menu, Pin, PinOff, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth-context";
import { getRoom, getRoomToken, joinRoom, moderate, endRoom, fmtErr, leaveSession, sessionHeartbeat, djAction, transferHost, lockRoom as lockRoomApi, setAlwaysCameraOn, setMyRoomDisplayName, startClosing, cancelClosing, renameParticipant as renameApi, requestCamera, listCameraRequests, approveCameraRequest, denyCameraRequest, inviteToCamera, revokeCameraByUser, refreshRoomToken, postSubscribeTick, djHeartbeat, extendRoom, getRoomRoster, selfReportWaitingRoom, returnFromWaitingRoom, resolveRoomInvite } from "../lib/api";
import GuestDoor from "../components/GuestDoor";
import WaitingRoom from "../components/WaitingRoom";
import SnapshotGrid from "../components/SnapshotGrid";
import VideoGrid from "../components/VideoGrid";
import DjConsole2 from "../components/DjConsole2";
import { useDjEngine } from "../audio/useDjEngine";
import { useShareAudio } from "../audio/useShareAudio";
import ChatPanel, { ChatTrigger, TileReactPicker } from "../components/ChatPanel";
import ParticipantsPanel, { ParticipantsTrigger } from "../components/ParticipantsPanel";
import BottomDock from "../components/BottomDock";
import PreRoomPreview from "./PreRoomPreview";
import AudienceJoinCard from "../components/AudienceJoinCard";
import IdleBanner from "../components/IdleBanner";
import CrowdAvatarTile from "../components/CrowdAvatarTile";
import { OverlayProvider, useOverlay, useRegisterOverlay } from "../components/OverlayContext";
import DarkSelect from "../components/DarkSelect";
import HostApprovalTray from "../components/HostApprovalTray";
import CameraRequestsTray from "../components/CameraRequestsTray";
import RoomFullCard from "../components/RoomFullCard";
import RulesEditor from "../components/RulesEditor";
import HostManageDropdown from "../components/HostManageDropdown";
import ShareAudioPreModal, { shareAudioModalDismissed } from "../components/ShareAudioPreModal";
import RoomContentGuard from "../components/RoomContentGuard";
import { useDocumentTitle } from "../lib/useDocumentTitle";
import confetti from "canvas-confetti";
import {
  MeetingTimer, SecurityShield, AudioSettings, LockButton, ClosingControls,
  ClosingBanner, EndedScreen, RenameModal,
} from "../components/RoomHeaderControls";

const TILES_PER_PAGE = 25;
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // refresh at T-5min

function parseRoleMetadata(metadataStr) {
  try { return metadataStr ? JSON.parse(metadataStr) : {}; } catch { return {}; }
}

// R18-7.1 — Top-level wrapper that mounts the OverlayProvider so any nested
// modal can register and trigger the force-unsubscribe-non-pinned sweep.
export default function RoomPage(props) {
  const { inviteCode } = useParams();
  const [resolvedRoomId, setResolvedRoomId] = useState(null);
  const [inviteError, setInviteError] = useState("");

  useEffect(() => {
    if (!inviteCode) return;
    let cancelled = false;
    resolveRoomInvite(inviteCode)
      .then((data) => { if (!cancelled) setResolvedRoomId(data.room_id); })
      .catch((err) => { if (!cancelled) setInviteError(fmtErr(err?.response?.data?.detail, "This private room link is unavailable.")); });
    return () => { cancelled = true; };
  }, [inviteCode]);

  if (inviteCode && inviteError) {
    return <div className="min-h-screen bg-[#050508] text-white grid place-items-center p-6"><div className="max-w-sm text-center rounded-2xl border border-white/10 bg-[#0c0c12] p-7"><AlertTriangle className="w-8 h-8 text-[#FF007F] mx-auto mb-3" /><h1 className="text-xl font-bold">Private link unavailable</h1><p className="text-sm text-white/55 mt-2">{inviteError}</p></div></div>;
  }
  if (inviteCode && !resolvedRoomId) {
    return <div className="min-h-screen bg-[#050508] text-white grid place-items-center"><Loader2 className="w-7 h-7 animate-spin text-[#00F0FF]" /></div>;
  }
  return (
    <OverlayProvider>
      <RoomPageInner {...props} resolvedRoomId={resolvedRoomId} />
    </OverlayProvider>
  );
}

function RoomPageInner({ resolvedRoomId = null }) {
  const { roomId: routeRoomId } = useParams();
  const roomId = resolvedRoomId || routeRoomId;
  const navigate = useNavigate();
  const { user } = useAuth();

  // Phase state: connecting | waiting | banned | expired | error | connected
  const [phase, setPhase] = useState("connecting");
  const [errMsg, setErrMsg] = useState("");
  const [roomMeta, setRoomMeta] = useState(null);
  const [waitingPos, setWaitingPos] = useState(0);
  const [participants, setParticipants] = useState([]);
  const [djInfo, setDjInfo] = useState({ name: null, track: null, playing: false });
  const [brb, setBrb] = useState(() => {
    // Hotfix — persist BRB across reload per spec note.
    try { return localStorage.getItem("sr-brb") === "1"; } catch { return false; }
  });
  // Mic state for DJ/host/cohost mic toggle in BottomDock
  const [micOn, setMicOn] = useState(false);
  // R22-MVP — Waiting room state. Participant is moved here on tab-away or host/cohost action.
  const [inWaitingRoom, setInWaitingRoom] = useState(false);
  const [waitingRoomReason, setWaitingRoomReason] = useState(null);
  const camOffCountRef = useRef(0); // legacy ref, kept for compatibility
  // R13-D — In-room Edit Devices flip-back. Mounts PreRoomPreview in edit mode
  // as an overlay. Selecting a new cam/mic hot-swaps via replaceTrack on the
  // already-published LiveKit track.
  const [editingDevices, setEditingDevices] = useState(false);
  // R14-A3 — Host approval tray for bot escalations.
  const [hostApprovalCards, setHostApprovalCards] = useState([]);
  // R18-5 — Camera Request state.
  //   tokenCanPublish      → driven by /api/rooms/{id}/token response
  //   myPendingRequestId   → audience-side, set when we POST /camera/request
  //   pendingRequests      → staff-side, list polled from /camera/requests
  //   roomFullCap          → set when /api/rooms/{id}/token returns 410 room_full
  const [tokenCanPublish, setTokenCanPublish] = useState(false);
  const [myPendingRequestId, setMyPendingRequestId] = useState(null);
  const [requestingCam, setRequestingCam] = useState(false);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [roomFullCap, setRoomFullCap] = useState(null);
  const [roomFullRetrying, setRoomFullRetrying] = useState(false);
  // Incremented after a successful session handoff so the existing connection
  // effect can join this room again without a browser reload or new sign-in.
  const [connectRetry, setConnectRetry] = useState(0);

  // R18-9 — Idle warning banner driven by `idle_warning` data message.
  //   idleBanner       → null when no warning active, else {minutes_until_end, ...}
  //   busyExtending    → debounces the Extend button
  const [idleBanner, setIdleBanner] = useState(null);
  const [busyExtending, setBusyExtending] = useState(false);

  // R18-4 — Crowd Wall roster (all room members including non-publishers).
  // Polled every 12s. The CrowdWall renders publishing participants via the
  // existing CamTile pipeline + non-publishing members via <CrowdAvatarTile>.
  const [roster, setRoster] = useState([]);

  // R18-7 — Per-tile subscription state. Keyed by remote identity, driven by
  // an IntersectionObserver attached to each CamTile root. Subscription toggles
  // explicitly via remoteParticipant.trackPublications[*].setSubscribed().
  // Stored in a ref so we don't trigger React renders on every toggle; the
  // 60s subscribe-tick effect reads ref.current.size for telemetry.
  const subscribedRef = useRef(new Set());
  const subscribeIntent = useCallback((identity, source, wantSubscribed) => {
    if (!identity) return;
    const r = lkRoomRef.current;
    // Instrumentation per R18-7 brief: log every call so tests can grep.
    // eslint-disable-next-line no-console
    console.log(`[R18-7] subscribeIntent ${identity} ${source}=${!!wantSubscribed}`);
    if (!r) return;
    const remote = r.remoteParticipants?.get?.(identity) || r.participants?.get?.(identity);
    if (!remote || !remote.trackPublications) return;
    let touched = 0;
    for (const pub of remote.trackPublications.values()) {
      const isCamera = (pub.source && String(pub.source).toLowerCase().includes("camera"))
        || (pub.kind && String(pub.kind).toLowerCase() === "video");
      const isAudio = (pub.source && String(pub.source).toLowerCase().includes("microphone"))
        || (pub.kind && String(pub.kind).toLowerCase() === "audio");
      // Safeguard: this helper is video-only by design. Audio subscriptions
      // (i.e. the always-on DJ mic) are never touched, so DJ audio stays
      // audible regardless of off-screen video unsubscribes.
      if (source === "video" && !isCamera) continue;
      if (source === "audio" && !isAudio) continue;
      try {
        if (typeof pub.setSubscribed === "function") {
          pub.setSubscribed(!!wantSubscribed);
          touched += 1;
        }
      } catch (err) {
        // Older SDKs may throw on unsupported flips; non-fatal.
      }
    }
    if (touched > 0) {
      const key = `${identity}:${source}`;
      if (wantSubscribed) subscribedRef.current.add(key);
      else subscribedRef.current.delete(key);
    }
  }, []);

  // R18-7.1 — pinned identities mirrored into a ref so visibility / overlay
  // sweeps always read the live set without depending on render cycles.
  const pinnedIdentitiesRef = useRef([]);

  // R18-7.1 — Force-unsubscribe all non-pinned REMOTE video tracks. Walks
  // every remoteParticipant's track publications and flips video off unless
  // the participant is pinned. Audio publications are deliberately UNTOUCHED
  // so DJ audio remains audible. Used by:
  //   - document.visibilityState === "hidden"
  //   - overlayCount > 0 (any blocking modal/overlay mounted)
  const forceUnsubscribeNonPinnedVideo = useCallback((reason) => {
    const r = lkRoomRef.current;
    if (!r) return 0;
    const pinned = new Set(pinnedIdentitiesRef.current || []);
    const remotes = r.remoteParticipants || r.participants;
    if (!remotes || typeof remotes.forEach !== "function") return 0;
    let count = 0;
    remotes.forEach((rp) => {
      const ident = rp?.identity;
      if (!ident || pinned.has(ident)) return;
      const pubs = rp.trackPublications;
      if (!pubs || typeof pubs.forEach !== "function") return;
      pubs.forEach((pub) => {
        const isCamera = (pub.source && String(pub.source).toLowerCase().includes("camera"))
          || (pub.kind && String(pub.kind).toLowerCase() === "video");
        if (!isCamera) return;
        try {
          if (typeof pub.setSubscribed === "function" && pub.isSubscribed !== false) {
            pub.setSubscribed(false);
            count += 1;
            subscribedRef.current.delete(`${ident}:video`);
          }
        } catch { /* noop */ }
      });
    });
    if (count > 0 || reason) {
      // eslint-disable-next-line no-console
      console.log(`[R18-7.1] ${reason || "force-unsubscribe"} forcing video unsubscribe count=${count}`);
    }
    return count;
  }, []);

  // R18-8 — Pin slot stickiness: persist display_name + last-known role for
  // each pinned identity, so an offline pinned tile renders an avatar
  // placeholder ("Reconnecting…") instead of disappearing.
  const [pinnedMeta, setPinnedMeta] = useState({}); // identity → {display_name, role, is_dj}
  // R14.4 — display name returned alongside the LiveKit token. For guests
  // this is the name they typed in GuestDoor (lives in the sr_guest JWT);
  // for users it's their account display_name. PreRoomPreview reads this so
  // guests don't have to type their name twice.
  const [tokenDisplayName, setTokenDisplayName] = useState("");
  // R16-D — Local multi-pin gallery. Each viewer independently pins 0–9
  // participants by identity; the pinned set lives in a small grid above
  // the main gallery. NO room-wide sync — each user's view is their own.
  // Persisted in localStorage `sr-pinned-{room_id}-{my_identity}`.
  const [pinnedIdentities, setPinnedIdentities] = useState([]);
  // R16-D — Keyholder optional auto-pin-when-on-deck setting (per-user,
  // localStorage). Defaults to ON. When the local viewer takes the DJ
  // deck (current_dj_user_id === myIdentity), pin themselves to slot 0.
  // Setting key: `sr-autopin-on-deck-{user_id}`.
  const [autoPinOnDeck, setAutoPinOnDeck] = useState(true);
  // R14-D — Rule editor side panel open state.
  const [rulesEditorOpen, setRulesEditorOpen] = useState(false);
  // R13-C — Pre-Room Preview gate. Before we call connectLiveKit() we set
  // phase="preview" and stash the token. The user picks devices + clicks Enter,
  // which stores chosen device IDs in `chosenDevicesRef` and resumes the
  // existing connect chain with those IDs.
  const pendingTokenRef = useRef(null);
  const chosenDevicesRef = useRef(null);
  const connectLiveKitRef = useRef(null);
  const triggerConnect = useCallback(async (tok) => {
    pendingTokenRef.current = tok;
    // R18-Phase1 follow-up — Audiences in Lounge/Club have can_publish=false
    // and don't need the device-selection gate. Route them through the
    // lightweight `<AudienceJoinCard>` (no getUserMedia) instead of the
    // legacy PreRoomPreview. The Request Camera flow inside Room.jsx will
    // prompt for the camera at the actual moment the host approves.
    // All participants can publish camera now; no audience-only path.
    const isAudienceListener = false;
    setPhase(isAudienceListener ? "audience_join" : "preview");
  }, []);
  const [page, setPage] = useState(0);
  const [copied, setCopied] = useState(false);
  const [appRole, setAppRole] = useState("none"); // host | cohost | participant
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [chatUnread, setChatUnread] = useState(0);
  const [participantsOpen, setParticipantsOpen] = useState(false);
  const [galleryView, setGalleryView] = useState("default"); // Phase 3.1 sort
  // Video grid view mode: speaker | gallery | multiple
  const [gridViewMode, setGridViewMode] = useState(() => {
    try { return localStorage.getItem(`sr-grid-view-${roomId}`) || "gallery"; } catch { return "gallery"; }
  });
  const [activeSpeakerIdentity, setActiveSpeakerIdentity] = useState(null);
  // Phase 3.3 — set document title to "Full Spectrum Center · <room name>" while in a room.
  useDocumentTitle(roomMeta?.name || (phase === "loading" ? "Loading room…" : null));
  const [devMode, setDevMode] = useState(() => {
    try { return localStorage.getItem("sr-dev-mode") === "1"; } catch { return false; }
  });
  // Persist gallery sort per-room
  useEffect(() => {
    try { const v = localStorage.getItem(`sr-gallery-sort-${roomId}`); if (v) setGalleryView(v); } catch { /* */ }
    try { const v = localStorage.getItem(`sr-grid-view-${roomId}`); if (v) setGridViewMode(v); } catch { /* */ }
  }, [roomId]);
  const setGallerySort = (mode) => {
    setGalleryView(mode);
    try { localStorage.setItem(`sr-gallery-sort-${roomId}`, mode); } catch { /* */ }
  };

  // R16-D — Auto-pin-on-deck Keyholder default flag. Needs no derived values
  // so we can declare here. (Hydrate fully from localStorage in the effect
  // below — depends on myIdentity which is computed later in this function.)
  const wasDjRef = useRef(false);
  // Phase A R8.2 — global error surfacing for the main tab. Any uncaught
  // exception or unhandled promise rejection is logged and surfaced as a
  // red toast. `sr-toast` custom events from deep code paths bridge to
  // sonner so the user sees the error instead of it being swallowed.
  useEffect(() => {
    const onError = (e) => {
      // R14.2-A — preventDefault + zero returnValue so CRA's dev error
      // overlay never takes over (the global handler in index.js also
      // does this in capture phase as a safety net for errors before
      // Room mounts). Then surface the error as a red toast so the
      // user still sees it.
      try { e.preventDefault(); } catch { /* */ }
      try { e.returnValue = ""; } catch { /* */ }
      // eslint-disable-next-line no-console
      console.error("[UNCAUGHT]", e.message, e.error);
      try { toast.error(`Internal error — ${e.message || "open console for details"}`); } catch { /* */ }
    };
    const onRejection = (e) => {
      try { e.preventDefault(); } catch { /* */ }
      try { e.returnValue = ""; } catch { /* */ }
      // eslint-disable-next-line no-console
      console.error("[UNCAUGHT promise]", e.reason);
      try { toast.error(`Internal error (promise) — ${e.reason?.message || e.reason || "open console for details"}`); } catch { /* */ }
    };
    const onSrToast = (e) => {
      const { type = "info", msg = "" } = e.detail || {};
      try {
        if (type === "error")        toast.error(msg);
        else if (type === "success") toast.success(msg);
        else if (type === "warning") toast.warning(msg);
        else                          toast.info(msg);
      } catch { /* */ }
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    window.addEventListener("sr-toast", onSrToast);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      window.removeEventListener("sr-toast", onSrToast);
    };
  }, []);


  // Phase A R9 C — listen for AudioSettingsPopover device-change events
  // and apply them to the live LiveKit room immediately. LiveKit 2.x
  // exposes `room.switchActiveDevice(kind, deviceId, exact?)` which
  // restarts the underlying MediaStreamTrack on the existing publication
  // — no unpublish/republish flicker.
  useEffect(() => {
    const onDevice = async (e) => {
      const { kind, deviceId } = e.detail || {};
      const r = lkRoomRef.current;
      if (!r || !kind) return;
      try {
        // eslint-disable-next-line no-console
        console.log(`[sr-device-change] kind=${kind} deviceId='${deviceId || "default"}' applying via lkRoom.switchActiveDevice`);
        await r.switchActiveDevice(kind, deviceId || undefined);
        try { toast.success(`${kind === "audiooutput" ? "Output" : kind === "videoinput" ? "Camera" : "Microphone"} switched`); } catch { /* */ }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[sr-device-change] switchActiveDevice failed:", err);
        try { toast.error(`Device switch failed: ${err?.message || err}`); } catch { /* */ }
      }
    };
    window.addEventListener("sr-device-change", onDevice);
    return () => window.removeEventListener("sr-device-change", onDevice);
  }, []);

  const toggleDevMode = () => setDevMode((v) => { const n = !v; try { localStorage.setItem("sr-dev-mode", n ? "1" : "0"); } catch { /* */ } return n; });
  // Phase A R11 C — header overflow menu state (visible at <lg).
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const chatHandlersRef = useRef(new Set());
  const chatPanelRef = useRef(null);
  // Phase 3 Stage 2 — per-tile emoji bursts + DJ-handoff 5-sec pulse
  const [tileBursts, setTileBursts] = useState({}); // identity -> [{id, emoji}]
  const [djPulse, setDjPulse] = useState({ identity: null, until: 0 });
  const burstClusterRef = useRef(new Map()); // identity|emoji -> {id, count, ts}
  const registerChatHandler = useCallback((fn) => {
    chatHandlersRef.current.add(fn);
    return () => chatHandlersRef.current.delete(fn);
  }, []);

  const spawnTileBurst = useCallback((identity, emoji) => {
    const now = Date.now();
    const key = `${identity}|${emoji}`;
    const existing = burstClusterRef.current.get(key);
    if (existing && now - existing.ts < 500) {
      existing.count += 1;
      existing.ts = now;
      setTileBursts((prev) => {
        const list = prev[identity] || [];
        return { ...prev, [identity]: list.map((b) => b.id === existing.id ? { ...b, count: existing.count } : b) };
      });
      return;
    }
    const id = "b_" + now + "_" + Math.random().toString(36).slice(2, 7);
    burstClusterRef.current.set(key, { id, count: 1, ts: now });
    const drift = Math.round((Math.random() - 0.5) * 40); // ±20px
    setTileBursts((prev) => {
      const list = prev[identity] || [];
      return { ...prev, [identity]: [...list, { id, emoji, drift, count: 1 }] };
    });
    setTimeout(() => {
      setTileBursts((prev) => {
        const list = prev[identity] || [];
        return { ...prev, [identity]: list.filter((b) => b.id !== id) };
      });
      burstClusterRef.current.delete(key);
    }, 2200);
  }, []);

  const broadcastTileReact = useCallback(async (targetIdentity, emoji) => {
    spawnTileBurst(targetIdentity, emoji); // optimistic local
    const lr = lkRoomRef.current;
    const myId = lr?.localParticipant?.identity;
    const msg = {
      type: "tile_react",
      target_identity: targetIdentity,
      emoji,
      by_identity: myId || "unknown",
      ts: Date.now(),
    };
    let sent = false;
    if (lr && lr.localParticipant && lr.state === "connected") {
      try {
        const payload = new TextEncoder().encode(JSON.stringify(msg));
        lr.localParticipant.publishData(payload, { reliable: true });
        sent = true;
      } catch (e) {
        console.warn("[react] publishData failed, falling back to API:", e?.message);
      }
    }
    // Fallback: broadcast via backend API when LiveKit is not connected
    if (!sent && roomId) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/rooms/${roomId}/broadcast`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(msg),
        });
      } catch { /* best effort */ }
    }
  }, [spawnTileBurst, roomId]);
  const [showMod, setShowMod] = useState(false);

  // Phase 2.2.x — diagnostics. Authoritative identity from the connected LK Room
  // (not from `user`, which is undefined for guests), plus a ring buffer of the
  // last 5 data messages we received, so the user can see live state on a
  // screenshot without opening DevTools.
  const [localIdentity, setLocalIdentity] = useState(null);
  const [recentDataMsgs, setRecentDataMsgs] = useState([]); // [{ type, at, from }]
  const [diagExpanded, setDiagExpanded] = useState(false);
  const [resyncing, setResyncing] = useState(false);
  // Phase 2.3 — soft-close / rename / ended state
  const [renameTarget, setRenameTarget] = useState(null); // {identity, name}
  const [endedInfo, setEndedInfo] = useState(null); // {ended_at, can_reopen}

  // View mode (host only). DJ_ONLY | SPLIT | ROOM_ONLY. Persisted per-user in localStorage.
  const [viewMode, setViewMode] = useState("ROOM_ONLY");
  const [splitPct, setSplitPct] = useState(60); // % width of left (console) pane in SPLIT mode
  const splitDragRef = useRef(false);
  const splitContainerRef = useRef(null);

  useEffect(() => {
    if (!user || user === false) return;
    try {
      const stored = localStorage.getItem(`sr-viewmode:${user.user_id}`);
      if (stored === "DJ_ONLY" || stored === "SPLIT" || stored === "ROOM_ONLY") setViewMode(stored);
      const sp = parseFloat(localStorage.getItem(`sr-splitpct:${user.user_id}`));
      if (!isNaN(sp) && sp >= 20 && sp <= 80) setSplitPct(sp);
    } catch { /* ignore */ }
  }, [user]);

  const applyViewMode = useCallback((m) => {
    setViewMode(m);
    try {
      if (user && user.user_id) localStorage.setItem(`sr-viewmode:${user.user_id}`, m);
    } catch { /* ignore */ }
  }, [user]);

  // Divider drag
  const onSplitDown = useCallback((e) => {
    splitDragRef.current = true;
    e.preventDefault();
    const onMove = (ev) => {
      if (!splitDragRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const x = (ev.touches ? ev.touches[0].clientX : ev.clientX) - rect.left;
      let pct = (x / rect.width) * 100;
      pct = Math.max(20, Math.min(80, pct));
      // Snap to 30/50/70 within ±2.5%
      for (const snap of [30, 50, 70]) {
        if (Math.abs(pct - snap) < 2.5) { pct = snap; break; }
      }
      setSplitPct(pct);
    };
    const onUp = () => {
      splitDragRef.current = false;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
      try {
        if (user && user.user_id) localStorage.setItem(`sr-splitpct:${user.user_id}`, String(splitPctRef.current));
      } catch { /* ignore */ }
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  }, [user]);

  // Phase 2.2 — single-active-room conflict
  const [conflict, setConflict] = useState(null); // { current_room_id, current_room_name }
  const [switching, setSwitching] = useState(false);

  // Heartbeat while in this room — pings every 15s for faster stale cleanup.
  useEffect(() => {
    if (appRole === "none" || phase !== "connected") return;
    const id = setInterval(() => { sessionHeartbeat(roomId); }, 15_000);
    sessionHeartbeat(roomId); // immediate ping
    return () => clearInterval(id);
  }, [appRole, phase, roomId]);

  // Moderation status poll fallback. When LiveKit data messages fail to deliver
  // (e.g. LiveKit Cloud room expired), this poll catches kick_to_waiting,
  // remove_until_reset, AND room_ended within 10 seconds.
  useEffect(() => {
    if (phase !== "connected" || inWaitingRoom) return;
    const poll = async () => {
      try {
        const t = await getRoomToken(roomId);
        if (!t.ok && t.kind === "forbidden") {
          setPhase("banned");
          setErrMsg(t.error?.message || "Removed by moderator.");
          try { lkRoomRef.current?.disconnect(); } catch { /* */ }
        } else if (!t.ok && (t.kind === "ended" || t.kind === "closed")) {
          // A permanent room close ends this event in every already open tab.
          setEndedInfo({ ended_at: t.ended_at || new Date().toISOString(), can_reopen: false });
          setErrMsg(t.error?.message || "This event is closed.");
          setPhase("ended");
          try { lkRoomRef.current?.disconnect(); } catch { /* */ }
        } else if (t.ok && t.status === "waiting" && !(appRole === "host" || appRole === "cohost")) {
          setWaitingRoomReason("host_action");
          setInWaitingRoom(true);
        }
      } catch { /* network error, skip */ }
      // Also poll room metadata to detect closing_at countdown
      try {
        const meta = await getRoom(roomId);
        if (meta && meta.closing_at && !roomMeta?.closing_at) {
          setRoomMeta((prev) => ({ ...prev, closing_at: meta.closing_at }));
        }
        if (meta && (meta.status === "ended" || meta.is_open === false)) {
          setEndedInfo({ ended_at: meta.ended_at || new Date().toISOString(), can_reopen: false });
          setErrMsg(meta.status === "ended" ? "This room has ended." : "This event is closed.");
          setPhase("ended");
          try { lkRoomRef.current?.disconnect(); } catch { /* */ }
        }
      } catch { /* */ }
    };
    const id = setInterval(poll, 8_000);
    return () => clearInterval(id);
  }, [phase, roomId, appRole, inWaitingRoom, roomMeta?.closing_at]);

  // R14.4-D — beforeunload sendBeacon so the server learns about tab-close
  // before the 90s sweeper window. sendBeacon is fire-and-forget and is
  // guaranteed to be delivered on unload (unlike fetch with `keepalive`,
  // which is best-effort). The /leave route accepts an empty body when the
  // auth cookie is present.
  useEffect(() => {
    if (appRole === "none" || phase !== "connected") return;
    const onBeforeUnload = () => {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/sessions/leave`;
        const payload = new Blob(
          [JSON.stringify({ room_id: roomId })],
          { type: "application/json" },
        );
        if (navigator.sendBeacon) {
          navigator.sendBeacon(url, payload);
        }
      } catch { /* swallow — best-effort */ }
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    window.addEventListener("pagehide", onBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      window.removeEventListener("pagehide", onBeforeUnload);
    };
  }, [appRole, phase, roomId]);

  // Refresh authoritative room state after any missed LiveKit role signal.
  useEffect(() => {
    if (phase !== "connected") return;
    const syncRoomState = () => { getRoom(roomId).then(setRoomMeta).catch(() => {}); };
    syncRoomState();
    const id = setInterval(syncRoomState, 5000);
    return () => clearInterval(id);
  }, [phase, roomId]);

  // Phase 2.3 — Auto-end the room when the closing_at timestamp passes — only
  // the HOST calls /end (one writer, no contention). Other clients learn via
  // the room_ended data message + the next /token poll → 410 → EndedScreen.
  useEffect(() => {
    if (appRole !== "host") return;
    const t = roomMeta?.closing_at ? new Date(roomMeta.closing_at).getTime() : null;
    if (!t) return;
    const ms = Math.max(0, t - Date.now());
    const id = setTimeout(() => { endRoom(roomId).catch(() => {}); }, ms);
    return () => clearTimeout(id);
  }, [roomMeta?.closing_at, appRole, roomId]);

  // R14.4 — Hydrate the HostApprovalTray from the server's pending-cards
  // endpoint on mount and poll every 8s. The original implementation only
  // populated `hostApprovalCards` from LiveKit data messages, which meant a
  // host who joined AFTER an escalation card was emitted (or refreshed the
  // page) would never see the backlog. Hydration is staff-gated server-side
  // (403 for non-staff), so we only call it when appRole is staff.
  useEffect(() => {
    if (phase !== "connected") return;
    if (appRole !== "host" && appRole !== "cohost") return;
    let cancelled = false;
    const fetchPending = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms/${roomId}/host-approval/pending`, {
          credentials: "include",
        });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const serverCards = data.cards || [];
        setHostApprovalCards((prev) => {
          // Merge: keep prev cards (LiveKit-delivered) that aren't yet in serverCards (race),
          // and append serverCards that aren't already in prev. Avoid duplicates by card_id.
          const ids = new Set(prev.map((c) => c.card_id));
          const merged = [...prev];
          for (const c of serverCards) {
            if (!ids.has(c.card_id)) merged.push(c);
          }
          return merged;
        });
      } catch { /* swallow: best-effort */ }
    };
    fetchPending();
    const id = setInterval(fetchPending, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, appRole, roomId]);

  const onSwitchRooms = async () => {
    if (!conflict) return;
    setSwitching(true);
    try {
      await leaveSession(conflict.current_room_id);
      setConflict(null);
      setPhase("connecting");
      // The connection effect performs the normal join and token checks again.
      // This preserves the member's Center session and avoids a hard reload.
      setConnectRetry((attempt) => attempt + 1);
    } catch (e) {
      toast.error("Couldn't leave the other room. " + fmtErr(e?.response?.data?.detail));
      setSwitching(false);
    }
  };

  // Phase 2.2 — gallery hover handoff. Sends the dj action; the actual mount/unmount
  // is driven by the `dj_change` LiveKit data message we already subscribed to above.
  const handleDjAction = useCallback(async (action, targetId, label) => {
    try {
      await djAction(roomId, action, targetId);
      getRoom(roomId).then(setRoomMeta).catch(() => {});
      toast.success(label);
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail, "DJ change failed"));
    }
  }, [roomId]);

  // Phase 2.2 — transfer host (host → cohost swap)
  const [hostTransfer, setHostTransfer] = useState(null); // { targetId, targetName }
  const handleConfirmTransferHost = async () => {
    if (!hostTransfer) return;
    const { targetId, targetName } = hostTransfer;
    setHostTransfer(null);
    try {
      await transferHost(roomId, targetId);
      getRoom(roomId).then(setRoomMeta).catch(() => {});
      toast.success(`${targetName} is now the host. You're a cohost.`);
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail, "Couldn't transfer host"));
    }
  };
  const handleTransferHostRequest = useCallback(async (targetId, targetName) => {
    if (!window.confirm(`Make ${targetName || "this cohost"} the host? You will become a cohost.`)) return;
    try {
      await transferHost(roomId, targetId);
      const meta = await getRoom(roomId);
      setRoomMeta(meta);
      toast.success(`${targetName || "Cohost"} is now the host. You're a cohost.`);
    } catch (e) {
      toast.error(fmtErr(e?.response?.data?.detail, "Couldn't transfer host"));
    }
  }, [roomId]);

  // DJ deck refs/state (host only)
  const [djFileName, setDjFileName] = useState("");
  const djAudioElRef = useRef(null);
  const djAudioCtxRef = useRef(null);
  const djSourceNodeRef = useRef(null);
  const djDestNodeRef = useRef(null);
  const djPublishedTrackRef = useRef(null);
  const djAudioObjectUrlRef = useRef(null);
  const [djPlaying, setDjPlaying] = useState(false);

  const remoteDjAudioRef = useRef(null);
  const lkRoomRef = useRef(null);
  const refreshTimerRef = useRef(null);

  // ---------- Connect ----------
  useEffect(() => {
    // Wait for auth state to be resolved (null = still checking; false = anon; object = logged in)
    if (user === null) return;

    // ANONYMOUS visitor handled BELOW inside go() — see R14.4-B note.
    // This was previously a separate early-return branch which never
    // reached `connectLiveKitRef.current = connectLiveKit` so guests
    // hit "no pending token" after Pre-Room Preview's Enter Room.

    // AUTHENTICATED user from here on (host/cohost). Use getRoom() to fetch meta.
    let cancelled = false;

    async function go() {
      try {
        // R14.4-B — guest fast-path. Skip getRoom/joinRoom (require auth)
        // and go straight to token mint + triggerConnect. After this
        // function returns the connectLiveKit declaration + ref
        // assignment below execute exactly as for the auth path, so
        // Enter Room can resume the connect chain.
        if (user === false) {
          const tok = await getRoomToken(roomId);
          if (cancelled) return;
          if (tok.ok && tok.status === "ok") {
            setAppRole(tok.app_role);
            // R18-3 — Capture canPublish so the in-room audience UI can decide
            // whether to render the Request Camera CTA.
            setTokenCanPublish(!!tok.can_publish);
            if (tok.display_name) setTokenDisplayName(tok.display_name);
            await triggerConnect(tok);
            return;
          }
          if (tok.kind === "forbidden") {
            setPhase("guest-banned");
            return;
          }
          if (tok.kind === "ended" || tok.kind === "closed") {
            setEndedInfo({ ended_at: tok.ended_at || new Date().toISOString(), can_reopen: false });
            setErrMsg(tok.error?.message || "This event is closed.");
            setPhase("ended");
            return;
          }
          if (tok.kind === "guest_entry_required") { setPhase("guest-door"); return; }
          if (tok.kind === "expired") { setPhase("expired"); return; }
          if (tok.kind === "already_in_room") {
            setConflict({ current_room_id: tok.error?.current_room_id, current_room_name: tok.error?.current_room_name });
            setPhase("conflict");
            return;
          }
          // Anything else → GuestDoor for Turnstile + name input.
          setPhase("guest-door");
          return;
        }

        const meta = await getRoom(roomId);
        if (cancelled) return;
        setRoomMeta(meta);

        // Phase 2.3 — closed-room lockdown. If the room is already ended on
        // page load, short-circuit straight to EndedScreen (with reopen CTA
        // for the host). The token endpoint will also 410, but routing here
        // first means we never even attempt the join/connect flow.
        if (meta?.status === "ended" || meta?.is_open === false) {
          setEndedInfo({
            ended_at: meta.ended_at || new Date().toISOString(),
            can_reopen: false,
          });
          setErrMsg(meta?.status === "ended" ? "This room has ended." : "This event is closed. Please return to the Center and wait for the room manager to open a new event.");
          setPhase("ended");
          return;
        }

        // 1) join → approved | waiting | error
        const join = await joinRoom(roomId).catch((e) => {
          if (e?.response?.status === 410) {
            setEndedInfo({ ended_at: new Date().toISOString(), can_reopen: false });
            setPhase("ended");
            setErrMsg("This event is closed. Please return to the Center.");
            return null;
          }
          if (e?.response?.status === 403) {
            setPhase("banned");
            setErrMsg(fmtErr(e.response.data?.detail, "You've been removed until the host resets the room."));
            return null;
          }
          if (e?.response?.status === 404) {
            setPhase("error");
            setErrMsg("Room not found.");
            return null;
          }
          throw e;
        });
        if (!join || cancelled) return;

        if (join.status === "waiting") {
          setWaitingPos(join.position || 0);
          setPhase("waiting");
          return;
        }

        // 2) token
        const tok = await getRoomToken(roomId);
        if (cancelled) return;
        if (!tok.ok) {
          if (tok.kind === "lk_unconfigured") {
            setPhase("error");
            setErrMsg(tok.error?.message || "LiveKit not configured.");
            return;
          }
          if (tok.kind === "closed" || tok.kind === "ended") {
            setEndedInfo({ ended_at: tok.ended_at || new Date().toISOString(), can_reopen: false });
            setPhase("ended");
            setErrMsg(tok.error?.message || "This event is closed.");
            return;
          }
          if (tok.kind === "guest_entry_required") {
            setPhase("guest-door");
            return;
          }
          if (tok.kind === "expired") {
            setPhase("expired");
            setErrMsg(tok.error?.message || "Room has ended.");
            return;
          }
          if (tok.kind === "forbidden") {
            setPhase("banned");
            setErrMsg(tok.error?.message || "You've been removed.");
            return;
          }
          if (tok.kind === "already_in_room") {
            setConflict({ current_room_id: tok.error?.current_room_id, current_room_name: tok.error?.current_room_name });
            setPhase("conflict");
            return;
          }
          if (tok.kind === "room_full") {
            // R18-10 — hard cap reached. Show RoomFullCard with a retry CTA;
            // a spot opens up as participants leave.
            setRoomFullCap(typeof tok.cap === "number" ? tok.cap : null);
            setPhase("room_full");
            return;
          }
          setPhase("error");
          setErrMsg("Couldn't issue token.");
          return;
        }
        if (tok.status === "waiting") {
          setWaitingPos(tok.position || 0);
          setPhase("waiting");
          return;
        }

        setAppRole(tok.app_role);
        // R18-3 — Capture canPublish so the audience UI can show the
        // Request Camera CTA in Lounge/Club mode.
        setTokenCanPublish(!!tok.can_publish);
        if (tok.display_name) setTokenDisplayName(tok.display_name);
        await triggerConnect(tok);
      } catch (e) {
        console.error(e);
        if (!cancelled) {
          setPhase("error");
          setErrMsg(fmtErr(e?.response?.data?.detail, e?.message || "Failed to join room"));
        }
      }
    }

    async function connectLiveKit(tok) {
      const room = new Room({ adaptiveStream: true, dynacast: true });
      lkRoomRef.current = room;
      room
        .on(RoomEvent.Connected, () => {
          setPhase("connected");
          // Authoritative local identity — what LiveKit actually sees. This is the
          // string we compare against roomMeta.current_dj_user_id, so the
          // diagnostics pill (and isCurrentDj gate) must use this exact value.
          try { setLocalIdentity(room.localParticipant?.identity || null); } catch { /* ignore */ }
          rebuildParticipants(room);
        })
        .on(RoomEvent.Disconnected, async (reason) => {
          // Phase 2.2.x — Robust post-disconnect routing.
          // When the host calls /moderate {action:"kick_to_waiting"}, the
          // backend immediately calls LiveKit.remove_participant which fires
          // this Disconnected event on the kicked client — typically BEFORE
          // the broadcast `moderation` data message can be delivered (the SFU
          // tears down the data channel as part of the eviction). Without
          // this handler the client just sits on a dead connection.
          //
          // Server is the source of truth. We re-ask "what's my status now?"
          // via /api/rooms/{id}/token and route accordingly.
          // eslint-disable-next-line no-console
          console.log("[SR] disconnected, reason:", reason);
          try {
            const t = await getRoomToken(roomId);
            if (!t.ok && t.kind === "forbidden") {
              setErrMsg(t.error?.message || "Removed by moderator.");
              setPhase("banned");
              return;
            }
            if (t.ok && t.status === "waiting") {
              setWaitingPos(t.position || 0);
              setPhase("waiting");
              return;
            }
            if (!t.ok && (t.kind === "expired" || t.kind === "ended")) {
              setPhase("expired");
              return;
            }
            // Conflict, network blip, or transient — flag for the user.
            if (!t.ok && t.kind === "conflict") {
              setPhase("conflict");
              return;
            }
            // We still have a valid token; treat as a transient drop.
            // eslint-disable-next-line no-console
            console.warn("[SR] disconnected with still-valid token; staying on screen");
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn("[SR] post-disconnect token fetch failed", e);
            setErrMsg("Lost connection to the room.");
            setPhase("error");
          }
        })
        .on(RoomEvent.ParticipantConnected, () => rebuildParticipants(room))
        .on(RoomEvent.ParticipantDisconnected, () => rebuildParticipants(room))
        .on(RoomEvent.ParticipantMetadataChanged, (_prev, participant) => {
          // Hotfix — late-joiner + cross-tab BRB. When a remote participant's
          // metadata changes, sync their `brb` flag into our local state so
          // their tile overlay flips without waiting for a fresh data message.
          try {
            const m = parseRoleMetadata(participant.metadata);
            setParticipants((prev) => prev.map((p) =>
              p.identity === participant.identity ? { ...p, brb: !!m.brb } : p
            ));
          } catch { /* ignore */ }
        })
        .on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
          rebuildParticipants(room);
          const m = parseRoleMetadata(participant.metadata);
          if (track.kind === Track.Kind.Audio && m.role === "dj") {
            if (remoteDjAudioRef.current) {
              track.attach(remoteDjAudioRef.current);
              remoteDjAudioRef.current.play().catch(() => {});
            }
            setDjInfo((s) => ({
              ...s,
              name: m.display_name || participant.name || participant.identity,
              track: publication.trackName || "Live mix",
              playing: true,
            }));
          }
        })
        .on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
          rebuildParticipants(room);
          const m = parseRoleMetadata(participant.metadata);
          if (track.kind === Track.Kind.Audio && m.role === "dj") {
            track.detach();
            setDjInfo((s) => ({ ...s, playing: false, track: null }));
          }
        })
        .on(RoomEvent.ActiveSpeakersChanged, (speakers) => {
          // Track the current active speaker for Speaker View mode
          if (speakers && speakers.length > 0) {
            setActiveSpeakerIdentity(speakers[0].identity);
          }
        })
        .on(RoomEvent.DataReceived, (payload, participant) => {
          try {
            const msg = JSON.parse(new TextDecoder().decode(payload));
            console.log("[SR] data msg:", msg, "from:", participant?.identity, "myIdentity:", room.localParticipant?.identity);
            // Diagnostics ring buffer — keep last 5 messages so the in-app pill
            // can show what arrived (or didn't), without DevTools.
            try {
              const stamp = new Date().toLocaleTimeString([], { hour12: false });
              setRecentDataMsgs((prev) => [
                { type: msg.type || "unknown", at: stamp, from: participant?.identity || "server" },
                ...prev,
              ].slice(0, 5));
            } catch { /* ignore */ }
            if (msg.type === "brb") {
              setParticipants((prev) => prev.map((p) =>
                p.identity === participant?.identity ? { ...p, brb: !!msg.value } : p
              ));
            } else if (msg.type === "camera_policy") {
              const enabled = !!msg.always_camera_on;
              setRoomMeta((prev) => prev ? { ...prev, always_camera_on: enabled } : prev);
              if (participant?.identity !== room.localParticipant?.identity) {
                toast.message(enabled ? "Host enabled automatic camera start for new joins" : "Host made cameras optional for new joins");
              }
            } else if (msg.type === "idle_warning") {
              // R18-9 — Idle sweeper broadcast. Show banner; host gets Extend btn.
              setIdleBanner({
                minutes_until_end: msg.minutes_until_end,
                warn_minutes: msg.warn_minutes,
                end_minutes: msg.end_minutes,
                at: msg.at,
              });
            } else if (msg.type === "idle_extended") {
              setIdleBanner(null);
              toast.success("Room extended");
            } else if (msg.type === "room_ended" && msg.reason === "idle_no_dj_signal") {
              toast.message("Room ended — DJ was silent");
              setIdleBanner(null);
              setPhase("expired");
              setErrMsg("Room auto-ended due to DJ inactivity.");
            } else if (msg.type === "camera_decision") {
              // R18-5 — audience auto-refresh on host decision.
              try {
                const myLkId = room.localParticipant?.identity;
                const targetIsMe = msg.target && msg.target === myLkId;
                if (targetIsMe) {
                  if (msg.decision === "approved" || msg.decision === "invited") {
                    toast.success(msg.decision === "invited" ? "Host invited you on cam" : "Camera approved");
                  } else if (msg.decision === "denied") {
                    toast.message("Camera request denied");
                  } else if (msg.decision === "revoked") {
                    toast.message("Camera turned off by host");
                  }
                  // Always clear the local pending pill when our request resolves.
                  setMyPendingRequestId(null);
                  // Re-mint our token so canPublish flips and we can publish camera.
                  // The token refresh is enough — the LiveKit room's publish
                  // permissions check happens server-side; the new JWT travels
                  // via the next setCameraEnabled call.
                  (async () => {
                    try {
                      const t = await refreshRoomToken(roomId).catch(() => null);
                      if (t && t.token) {
                        setTokenCanPublish(!!t.can_publish);
                        if (lkRoomRef.current && t.token) {
                          try { await lkRoomRef.current.updateToken(t.token); } catch (_) { /* legacy SDK */ }
                        }
                        // Auto-publish camera on approval/invite.
                        if ((msg.decision === "approved" || msg.decision === "invited")
                            && lkRoomRef.current?.localParticipant) {
                          try {
                            await lkRoomRef.current.localParticipant.setCameraEnabled(true);
                          } catch (err) {
                            console.warn("[R18-5] auto-publish after approval failed:", err);
                          }
                        }
                        if (msg.decision === "revoked" && lkRoomRef.current?.localParticipant) {
                          try {
                            await lkRoomRef.current.localParticipant.setCameraEnabled(false);
                          } catch (err) {
                            console.warn("[R18-5] auto-unpublish on revoke failed:", err);
                          }
                        }
                      }
                    } catch (err) {
                      console.warn("[R18-5] token refresh after decision failed:", err);
                    }
                  })();
                }
                // Staff: any decision invalidates the cached pending list — refetch.
                if (msg.decision === "approved" || msg.decision === "denied" || msg.decision === "invited" || msg.decision === "revoked") {
                  setPendingRequests((prev) => prev.filter((r) => r.request_id !== msg.request_id));
                }
              } catch (e) { console.warn("[R18-5] camera_decision handler error:", e); }
            } else if (msg.type === "host_approval_card") {
              // R14-A3 — staff get an escalation card. Append unless already present.
              const card = msg.card || {};
              if (card.card_id) {
                setHostApprovalCards((prev) =>
                  prev.find((c) => c.card_id === card.card_id) ? prev : [...prev, card]
                );
                // Auto-clear after 10 minutes if undecided.
                setTimeout(() => {
                  setHostApprovalCards((p) => p.filter((c) => c.card_id !== card.card_id));
                }, 10 * 60 * 1000);
              }
            } else if (msg.type === "bot_dm") {
              // R14-B/C — directed bot DM. Render as a toast on the addressed identity.
              try {
                const myLkId = room.localParticipant?.identity;
                if ((!msg.to || msg.to === myLkId) && msg.text) {
                  if (msg.tag === "warning") toast.warning(msg.text, { duration: 8000 });
                  else toast.message(msg.text, { description: `From ${(msg.bot||"bot").replace("_"," ")}`, duration: 6000 });
                }
              } catch { /* ignore */ }
            } else if (msg.type === "bot_post") {
              // Public bot post — extra toast for visibility, the message is also in chat.
              if (msg.text) toast.message(msg.text, { description: `${(msg.bot||"bot").replace("_"," ")} · room`, duration: 5000 });
            } else if (msg.type === "bot_rate_limit") {
              // R15-2 — NoSpamBot has told this client to back off. The
              // server-side enforcement is best-effort; the client honors
              // by surfacing a toast (chat panel listens to `sr-rate-limit`
              // event and disables input for `duration_sec`).
              try {
                const myLkId = room.localParticipant?.identity;
                if (!msg.to || msg.to === myLkId) {
                  const ds = Number(msg.duration_sec || 30);
                  toast.warning(msg.text, { duration: Math.min(ds * 1000, 8000) });
                  try {
                    window.dispatchEvent(new CustomEvent("sr-rate-limit", {
                      detail: { duration_sec: ds, at: msg.at },
                    }));
                  } catch { /* */ }
                }
              } catch { /* ignore */ }
            } else if (msg.type === "now_playing") {
              setDjInfo((s) => ({
                name: msg.dj || s.name,
                track: msg.track || s.track,
                playing: !!msg.playing,
              }));
            } else if (msg.type === "room_ended" || msg.type === "room_closed") {
              // A permanent room can be reused later, but this browser event is final.
              setEndedInfo({ ended_at: msg.at, can_reopen: false });
              setErrMsg("This event is closed. Please return to the Center.");
              setPhase("ended");
              try { room.disconnect(); } catch { /* ignore */ }
            } else if (msg.type === "moderation") {
              // Refresh room metadata to reflect new staff/banned/waiting state
              getRoom(roomId).then(setRoomMeta).catch(() => {});
              // If targeted at me, my next token-refresh will resolve to waiting/banned.
              // Compare against the LiveKit-authoritative identity (works for BOTH
              // auth users and guests; `user` is `false` for guests so the previous
              // `user.user_id` comparison silently never matched on guest tabs).
              const myLkId = room.localParticipant?.identity;
              if (msg.target === myLkId) {
                if (msg.action === "kick_to_waiting") {
                  // Show the WaitingRoom overlay with Turnstile re-auth
                  setWaitingRoomReason("host_action");
                  setInWaitingRoom(true);
                } else {
                  getRoomToken(roomId).then((t) => {
                    if (!t.ok && t.kind === "forbidden") {
                      setPhase("banned");
                      setErrMsg(t.error?.message || "Removed by moderator.");
                      try { room.disconnect(); } catch { /* ignore */ }
                    }
                  }).catch(() => {});
                }
              }
            } else if (msg.type === "dj_change") {
              // Phase 2.2 — DJ change. Refresh metadata so all UI gates re-evaluate.
              getRoom(roomId).then(setRoomMeta).catch(() => {});
              const myId = room.localParticipant?.identity;
              const newDjId = msg.new_dj_user_id;
              if (msg.previous_dj_user_id === myId) {
                toast.info("You're no longer the DJ");
                setDjInfo({ name: "", track: "", playing: false });
              } else if (newDjId === myId) {
                toast.success("You're now the DJ");
                applyViewMode("DJ_ONLY");
                // The assigned person may be a verified guest who entered by
                // room link. Refresh the current room credential in place so
                // the board and DJ media permission arrive without sign in.
                refreshRoomToken(roomId).then(async (tokenResult) => {
                  if (!tokenResult?.token) return;
                  setTokenCanPublish(!!tokenResult.can_publish);
                  try { await room.updateToken(tokenResult.token); } catch { /* the next token refresh retries */ }
                  getRoom(roomId).then(setRoomMeta).catch(() => {});
                }).catch(() => {});
                try {
                  confetti({ particleCount: 140, spread: 80, origin: { y: 0.55 }, colors: ["#FF007F", "#00F0FF", "#FFFFFF"], zIndex: 90 });
                  setTimeout(() => confetti({ particleCount: 70, spread: 120, origin: { x: 0.2, y: 0.6 }, colors: ["#FF007F", "#00F0FF"], zIndex: 90 }), 250);
                  setTimeout(() => confetti({ particleCount: 70, spread: 120, origin: { x: 0.8, y: 0.6 }, colors: ["#FF007F", "#00F0FF"], zIndex: 90 }), 500);
                } catch { /* */ }
                try { toast(`🎉 You're on the decks!${roomMeta?.name ? " · " + roomMeta.name : ""}`); } catch { /* */ }
              } else {
                setDjInfo((s) => ({ ...s, track: "", playing: false }));
              }
              // 5-sec cyan/magenta pulse on the new DJ's tile (everyone sees it).
              if (newDjId) setDjPulse({ identity: newDjId, until: Date.now() + 5000 });
            } else if (msg.type === "host_change") {
              // Refresh room metadata so all clients re-evaluate host/cohost gates.
              getRoom(roomId).then(setRoomMeta).catch(() => {});
              const myId = room.localParticipant?.identity;
              if (msg.new_host_user_id === myId) toast.success("You're now the host");
              else if (msg.previous_host_user_id === myId) toast.info("You're now a cohost");
            } else if (msg.type === "chat" || msg.type === "chat_edit" || msg.type === "chat_delete" || msg.type === "chat_reactions" || msg.type === "chat_thread_created" || msg.type === "mgmt_members_changed") {
              // Phase 3 — fan out chat-related data messages to subscribed ChatPanel hooks.
              chatHandlersRef.current.forEach((fn) => { try { fn(msg); } catch (e) { console.warn("chat handler failed", e); } });
              if (msg.type === "chat" && msg.channel === "public" && !chatOpen
                  && msg.message?.author_identity !== room.localParticipant?.identity) {
                setChatUnread((n) => n + 1);
              }
            } else if (msg.type === "tile_react") {
              // Phase 3 Stage 2 — per-tile emoji burst from another participant.
              if (msg.target_identity && msg.emoji) spawnTileBurst(msg.target_identity, msg.emoji);
            }
          } catch { /* ignore */ }
        });

      await room.connect(tok.url, tok.token);

      // R14.1-B — propagate the display name the user typed in PreRoomPreview
      // into BOTH the LiveKit participant name AND the metadata blob so tiles
      // pick it up via name → metadata.display_name → identity fallback.
      try {
        const chosenName = (chosenDevicesRef.current?.displayName || "").trim();
        if (chosenName) {
          try { await room.localParticipant.setName(chosenName); } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[display-name] setName failed:", err);
          }
          try {
            const existing = parseRoleMetadata(room.localParticipant.metadata);
            await room.localParticipant.setMetadata(JSON.stringify({ ...existing, display_name: chosenName }));
            // eslint-disable-next-line no-console
            console.log(`[display-name] applied name="${chosenName}" to LiveKit identity ${room.localParticipant.identity}`);
          } catch (err) {
            // eslint-disable-next-line no-console
            console.warn("[display-name] setMetadata failed:", err);
          }
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[display-name] propagation block error:", err);
      }

      // Publish camera. Host AND participants AND cohosts publish their cam.
      // R13-C: use the device IDs the user chose in PreRoomPreview (if any).
      // R18-Phase1: SKIP publish entirely when the JWT denies it (audience in
      // Lounge/Club). Their token's video.canPublish=false; LiveKit would
      // reject the publish anyway. More importantly, NOT calling
      // createLocalVideoTrack avoids the gratuitous getUserMedia prompt.
      // The camera_decision data-message handler upgrades the token + calls
      // setCameraEnabled(true) when the host approves, prompting for the
      // camera AT THAT MOMENT.
      if (tok && (tok.can_publish === false || tok.always_camera_on === false)) {
        // eslint-disable-next-line no-console
        console.log("[camera-policy] skipping automatic camera publish", {
          canPublish: tok.can_publish,
          alwaysCameraOn: tok.always_camera_on,
          mode: tok.mode || "?",
        });
      } else {
        try {
          const chosenCamId = chosenDevicesRef.current?.camId || null;
          const videoTrack = await createLocalVideoTrack({
            resolution: { width: 640, height: 360, frameRate: 24 },
            ...(chosenCamId ? { deviceId: { exact: chosenCamId } } : {}),
          });
          await room.localParticipant.publishTrack(videoTrack, {
            name: "camera", source: Track.Source.Camera,
          });
          // R13-C: route audio output to chosen speaker (if browser supports setSinkId)
          const chosenSpeakerId = chosenDevicesRef.current?.speakerId || null;
          if (chosenSpeakerId) {
            setTimeout(() => {
              document.querySelectorAll("audio,video").forEach((el) => {
                if (typeof el.setSinkId === "function") el.setSinkId(chosenSpeakerId).catch(() => {});
              });
            }, 250);
          }
        } catch (e) {
          console.warn("Camera publish failed:", e);
        }
      }
      rebuildParticipants(room);

      // Schedule auto-refresh
      const refreshIn = (tok.expires_in * 1000) - REFRESH_BEFORE_EXPIRY_MS;
      if (refreshIn > 0) {
        refreshTimerRef.current = setTimeout(async () => {
          const r = await getRoomToken(roomId);
          if (!r.ok || r.status === "waiting") {
            // status changed; let the next state poll handle it
            return;
          }
          // LiveKit JS doesn't support hot-swap of access token via livekit-client directly
          // in older versions; safest is to disconnect & reconnect. Modern versions support
          // room.options.token rotation, but to keep code simple here we reconnect.
          try { room.disconnect(); } catch { /* ignore */ }
          await room.connect(r.url, r.token);
        }, refreshIn);
      }
    }

    go();
    // R13-C: expose the inner connectLiveKit closure to outside-of-effect
    // callbacks so "Enter Room" can resume the connect chain that we paused.
    connectLiveKitRef.current = connectLiveKit;

    return () => {
      cancelled = true;
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      cleanupDjAudio();
      const r = lkRoomRef.current;
      if (r) { r.disconnect(); lkRoomRef.current = null; }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roomId, user, connectRetry]);

  // ---------- Participants reducer ----------
  const rebuildParticipants = useCallback((room) => {
    const list = [];
    const collect = (p, isLocal) => {
      const m = parseRoleMetadata(p.metadata);
      let videoPub = null;
      p.videoTrackPublications.forEach((pub) => {
        if (pub.source === Track.Source.Camera || pub.kind === Track.Kind.Video) {
          if (!videoPub) videoPub = pub;
        }
      });
      list.push({
        sid: p.sid || p.identity,
        identity: p.identity,
        // R14.1-B — priority order per spec: participant.name → metadata.display_name → identity.
        // p.name is what the user typed in PreRoomPreview (we call setName after connect).
        name: p.name || m.display_name || p.identity,
        role: m.role || "participant",
        appRole: m.app_role || "participant",
        avatarUrl: m.avatar_url || null,
        isLocal,
        videoPublication: videoPub,
        // Seed brb from each participant's metadata so late-joiners see the
        // current state on connect. Local participant's brb is sourced from
        // the local `brb` state (overridden below).
        brb: !!m.brb,
      });
    };
    collect(room.localParticipant, true);
    room.remoteParticipants.forEach((p) => collect(p, false));
    setParticipants((prev) => {
      const prevBrb = new Map(prev.map((x) => [x.identity, x.brb]));
      return list.map((p) => ({
        ...p,
        // Local participant: source of truth is the React `brb` state.
        // Remote participant: prefer metadata-derived brb (just collected),
        // fall back to last known prev value so a transient metadata gap
        // doesn't drop the overlay.
        brb: p.isLocal ? brb : (p.brb || prevBrb.get(p.identity) || false),
      }));
    });
    const dj = list.find((p) => p.role === "dj");
    if (dj) setDjInfo((s) => ({ ...s, name: dj.name }));
  }, [brb]);

  const sortedParticipants = useMemo(() => {
    const arr = [...participants];
    const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
    const byJoin = (a, b) => (a.joined_at || a.identity).localeCompare(b.joined_at || b.identity);
    const rolePriority = { dj: 0, host: 1, cohost: 2, listener: 3, participant: 3, guest: 3, none: 4 };
    const byRole = (a, b) => {
      const ra = a.role === "dj" ? 0 : a.appRole === "host" ? 1 : a.appRole === "cohost" ? 2 : 3;
      const rb = b.role === "dj" ? 0 : b.appRole === "host" ? 1 : b.appRole === "cohost" ? 2 : 3;
      return ra - rb;
    };
    switch (galleryView) {
      case "first_az": arr.sort(byName); break;
      case "first_za": arr.sort((a, b) => -byName(a, b)); break;
      case "first_in": arr.sort(byJoin); break;
      case "last_in":  arr.sort((a, b) => -byJoin(a, b)); break;
      case "role":     arr.sort((a, b) => byRole(a, b) || byName(a, b)); break;
      case "default":
      default:
        arr.sort((a, b) => {
          if (a.role !== b.role) return a.role === "dj" ? -1 : 1;
          if (a.appRole !== b.appRole) return a.appRole === "cohost" ? -1 : (b.appRole === "cohost" ? 1 : 0);
          if (a.isLocal !== b.isLocal) return a.isLocal ? -1 : 1;
          return byName(a, b);
        });
    }
    return arr;
  }, [participants, galleryView]);

  const totalPages = Math.max(1, Math.ceil(sortedParticipants.length / TILES_PER_PAGE));
  const pageSafe = Math.min(page, totalPages - 1);
  const pageItems = sortedParticipants.slice(pageSafe * TILES_PER_PAGE, pageSafe * TILES_PER_PAGE + TILES_PER_PAGE);

  // ---------- BRB ----------
  const toggleBrb = useCallback(() => {
    const next = !brb;
    setBrb(next);
    try { localStorage.setItem("sr-brb", next ? "1" : "0"); } catch { /* ignore */ }
    setParticipants((prev) => prev.map((p) => (p.isLocal ? { ...p, brb: next } : p)));
    const r = lkRoomRef.current;
    if (r) {
      // Two channels for robustness:
      //   1. publishData — instant push to everyone already connected.
      //   2. localParticipant.setMetadata — survives late-joiners (LiveKit
      //      delivers current metadata on connect and emits ParticipantMetadataChanged
      //      on later mutations).
      try {
        r.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify({ type: "brb", value: next })),
          { reliable: true }
        );
      } catch { /* ignore */ }
      try {
        const existing = parseRoleMetadata(r.localParticipant.metadata);
        r.localParticipant.setMetadata(JSON.stringify({ ...existing, brb: next }));
      } catch (err) {
        // setMetadata requires canUpdateOwnMetadata grant; data message alone still works.
        // eslint-disable-next-line no-console
        console.warn("[brb] setMetadata failed (data message still sent):", err);
      }
    }
  }, [brb]);

  // ---------- R14.1-C — CameraBot distributed self-detection worker ----------
  // Spawns the OffscreenCanvas-based worker, captures the local camera at 1 fps,
  // classifies each frame, and posts the rolling-majority state to the backend
  // every 10s. The backend BotRunner aggregates these and fires the warn/escalate
  // rules per the room's CameraBot rules.
  useEffect(() => {
    if (phase !== "connected") return;
    const room = lkRoomRef.current;
    if (!room) return;

    // eslint-disable-next-line no-console
    console.log("[cameraBot main] phase=connected, spawning worker");

    let worker;
    try {
      worker = new Worker(new URL("../workers/cameraBotWorker.js", import.meta.url));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[cameraBot main] worker spawn failed:", err);
      return;
    }

    let rolling = []; // last 10 samples
    let lastValues = null; // for debug logging
    worker.onmessage = (e) => {
      const d = e.data || {};
      if (d.kind === "cam_state") {
        lastValues = d.values || null;
        rolling.push(d.state);
        if (rolling.length > 10) rolling.shift();
        // eslint-disable-next-line no-console
        console.log(`[cameraBotWorker] state=${d.state}${lastValues ? ` luminance=${(lastValues.meanL||0).toFixed(3)} variance=${(lastValues.variance||0).toFixed(4)} edges=${(lastValues.edgeDensity||0).toFixed(3)}` : ""}`);
      }
    };
    worker.onerror = (err) => {
      // eslint-disable-next-line no-console
      console.error("[cameraBotWorker] worker error:", err.message || err);
    };

    // Sample a frame from the local video track every 1 second.
    const captureCanvas = document.createElement("canvas");
    captureCanvas.width = 320;
    captureCanvas.height = 180;
    const ctx = captureCanvas.getContext("2d", { willReadFrequently: true });
    const sampleVideo = document.createElement("video");
    sampleVideo.muted = true;
    sampleVideo.playsInline = true;
    sampleVideo.autoplay = true;
    let lastAttachedTrack = null;

    const tickSample = async () => {
      const lp = room.localParticipant;
      let videoTrack = null;
      lp.videoTrackPublications.forEach((pub) => {
        if (pub.source === Track.Source.Camera && pub.videoTrack && !videoTrack) {
          videoTrack = pub.videoTrack;
        }
      });
      if (!videoTrack || !videoTrack.mediaStreamTrack || videoTrack.isMuted) {
        // Camera is off: push camera_off straight into the rolling buffer.
        rolling.push("camera_off");
        if (rolling.length > 10) rolling.shift();
        // Camera-off no longer triggers waiting room (listeners are allowed).
        // eslint-disable-next-line no-console
        console.log(`[cameraBotWorker] no local cam \u2192 state=camera_off (listener mode OK)`);
        return;
      }
      // Camera is on — reset the cam-off counter.
      camOffCountRef.current = 0;
      try {
        const mst = videoTrack.mediaStreamTrack;
        if (lastAttachedTrack !== mst) {
          sampleVideo.srcObject = new MediaStream([mst]);
          await sampleVideo.play().catch(() => {});
          lastAttachedTrack = mst;
        }
        if (sampleVideo.videoWidth > 0) {
          ctx.drawImage(sampleVideo, 0, 0, captureCanvas.width, captureCanvas.height);
          const bitmap = await createImageBitmap(captureCanvas);
          worker.postMessage(
            { kind: "frame", bitmap, width: bitmap.width, height: bitmap.height },
            [bitmap]
          );
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("[cameraBot main] frame capture failed:", err.message || err);
      }
    };

    const sampleInterval = setInterval(tickSample, 1000);
    // Kick first sample now so debug log appears immediately on entry.
    tickSample();

    // Post the rolling-majority state every 10 seconds.
    const postInterval = setInterval(async () => {
      if (!rolling.length) return;
      const counts = {};
      rolling.forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
      const winner = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
      // eslint-disable-next-line no-console
      console.log(`[cameraBot main] posting state=${winner}  rolling=${rolling.join(",")}`);
      try {
        const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms/${encodeURIComponent(roomId)}/cam-state`, {
          method: "POST", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ state: winner, brb: !!brb }),
        });
        if (!resp.ok) {
          // eslint-disable-next-line no-console
          console.warn(`[cameraBot main] backend rejected cam-state: HTTP ${resp.status}`);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[cameraBot main] cam-state POST failed:", err);
      }
    }, 10_000);

    // Friendly toast so the user knows the bot is watching.
    try {
      toast.message("CameraBot is watching", {
        description: "If your cam is off or empty, you'll get a polite DM after about a minute.",
        duration: 6000,
      });
    } catch { /* */ }

    return () => {
      // eslint-disable-next-line no-console
      console.log("[cameraBot main] tearing down worker");
      clearInterval(sampleInterval);
      clearInterval(postInterval);
      try { sampleVideo.srcObject = null; } catch { /* */ }
      try { worker.terminate(); } catch { /* */ }
    };
  }, [phase, roomId, brb]);

  // R22-MVP — Tab-away detection for waiting room.
  // If user tabs away for 60+ seconds, move them to waiting room.
  // Only applies to regular participants (not host or cohost).
  useEffect(() => {
    if (phase !== "live" || appRole === "host" || appRole === "cohost") return;
    let hiddenAt = null;
    const TAB_AWAY_THRESHOLD = 60_000; // 60 seconds
    let timer = null;
    const handleVisibility = () => {
      if (document.hidden) {
        hiddenAt = Date.now();
        timer = setTimeout(() => {
          if (document.hidden && !inWaitingRoom) {
            setWaitingRoomReason("tab_away");
            setInWaitingRoom(true);
            selfReportWaitingRoom(roomId, "tab_away").catch(() => {});
          }
        }, TAB_AWAY_THRESHOLD);
      } else {
        hiddenAt = null;
        if (timer) { clearTimeout(timer); timer = null; }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (timer) clearTimeout(timer);
    };
  }, [phase, roomId, appRole, inWaitingRoom]);

  // R22-MVP — Snapshot uploader. Captures a low-res JPEG from the local camera
  // every 5 seconds and uploads it so other participants see a near-live
  // thumbnail in the SnapshotGrid without consuming LiveKit video bandwidth.
  useEffect(() => {
    if (phase !== "live" || inWaitingRoom) return;
    const INTERVAL = 5000;
    const WIDTH = 160;
    const HEIGHT = 120;
    const canvas = document.createElement("canvas");
    canvas.width = WIDTH;
    canvas.height = HEIGHT;
    const ctx = canvas.getContext("2d");
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    let stream = null;
    let timer = null;
    let stopped = false;

    const startCapture = async () => {
      try {
        // Get local video track from LiveKit room
        const r = lkRoomRef.current;
        const localVid = r?.localParticipant?.getTrackPublication?.("camera")?.track;
        if (localVid?.mediaStreamTrack) {
          stream = new MediaStream([localVid.mediaStreamTrack]);
        } else {
          // No local camera track available
          return;
        }
        video.srcObject = stream;
        await video.play().catch(() => {});
      } catch { return; }

      const capture = async () => {
        if (stopped) return;
        try {
          ctx.drawImage(video, 0, 0, WIDTH, HEIGHT);
          const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, "image/jpeg", 0.5)
          );
          if (blob && blob.size > 100 && blob.size < 50000) {
            await fetch(
              `${process.env.REACT_APP_BACKEND_URL}/api/rooms/${encodeURIComponent(roomId)}/snapshot`,
              { method: "POST", body: blob, credentials: "include",
                headers: { "Content-Type": "image/jpeg" } }
            ).catch(() => {});
          }
        } catch { /* ignore capture errors */ }
      };

      capture(); // First capture immediately
      timer = setInterval(capture, INTERVAL);
    };

    startCapture();
    return () => {
      stopped = true;
      if (timer) clearInterval(timer);
      try { video.srcObject = null; } catch { /* */ }
    };
  }, [phase, roomId, inWaitingRoom]);

  // ---------- DJ deck (host only) ----------
  const handleFilePicked = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (djAudioObjectUrlRef.current) URL.revokeObjectURL(djAudioObjectUrlRef.current);
    const url = URL.createObjectURL(file);
    djAudioObjectUrlRef.current = url;
    setDjFileName(file.name);
    if (djAudioElRef.current) {
      djAudioElRef.current.src = url;
      djAudioElRef.current.load();
    }
    broadcastNowPlaying({ track: file.name, playing: false });
  };

  const ensureDjGraph = async () => {
    if (!djAudioElRef.current) return null;
    if (!djAudioCtxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      djAudioCtxRef.current = new AC();
    }
    if (djAudioCtxRef.current.state === "suspended") await djAudioCtxRef.current.resume();
    if (!djSourceNodeRef.current) {
      djSourceNodeRef.current = djAudioCtxRef.current.createMediaElementSource(djAudioElRef.current);
      djDestNodeRef.current = djAudioCtxRef.current.createMediaStreamDestination();
      djSourceNodeRef.current.connect(djDestNodeRef.current);
      djSourceNodeRef.current.connect(djAudioCtxRef.current.destination); // monitor
    }
    return djDestNodeRef.current;
  };

  const ensureDjPublished = async () => {
    const r = lkRoomRef.current;
    if (!r || djPublishedTrackRef.current) return;
    const dest = await ensureDjGraph();
    if (!dest) return;
    const [mst] = dest.stream.getAudioTracks();
    if (!mst) return;
    const lkTrack = new LocalAudioTrack(mst);
    await r.localParticipant.publishTrack(lkTrack, {
      name: djFileName || "DJ Mix",
      source: Track.Source.Microphone,
    });
    djPublishedTrackRef.current = lkTrack;
  };

  const broadcastNowPlaying = ({ track, playing }) => {
    const r = lkRoomRef.current;
    if (r) {
      try {
        r.localParticipant.publishData(
          new TextEncoder().encode(JSON.stringify({
            type: "now_playing",
            dj: user.display_name,
            track: track ?? djFileName,
            playing: !!playing,
          })),
          { reliable: true }
        );
      } catch { /* ignore */ }
    }
    setDjInfo({ name: user.display_name, track: track ?? djFileName, playing: !!playing });
  };

  const handleDjPlay = async () => {
    if (!djAudioElRef.current?.src) return;
    await ensureDjPublished();
    try {
      await djAudioElRef.current.play();
      setDjPlaying(true);
      broadcastNowPlaying({ playing: true });
    } catch (e) { console.error(e); }
  };
  const handleDjPause = () => {
    djAudioElRef.current?.pause();
    setDjPlaying(false);
    broadcastNowPlaying({ playing: false });
  };
  const handleDjStop = () => {
    if (!djAudioElRef.current) return;
    djAudioElRef.current.pause();
    djAudioElRef.current.currentTime = 0;
    setDjPlaying(false);
    broadcastNowPlaying({ playing: false });
  };

  const cleanupDjAudio = () => {
    if (djPublishedTrackRef.current) {
      try { lkRoomRef.current?.localParticipant.unpublishTrack(djPublishedTrackRef.current); djPublishedTrackRef.current.stop(); } catch { /* ignore */ }
      djPublishedTrackRef.current = null;
    }
    try { djSourceNodeRef.current?.disconnect(); } catch { /* ignore */ }
    try { djDestNodeRef.current?.disconnect(); } catch { /* ignore */ }
    // R14.2-A — guard against double-close (Cannot close a closed AudioContext).
    const ctx = djAudioCtxRef.current;
    if (ctx && ctx.state !== "closed") {
      try { ctx.close(); } catch (e) {
        // eslint-disable-next-line no-console
        console.warn("[dj-audio] ctx.close threw:", e?.message || e);
      }
    }
    djSourceNodeRef.current = null;
    djDestNodeRef.current = null;
    djAudioCtxRef.current = null;
    if (djAudioObjectUrlRef.current) { URL.revokeObjectURL(djAudioObjectUrlRef.current); djAudioObjectUrlRef.current = null; }
  };

  const copyRoomId = async () => {
    // R13.1-D — share link uses ?as=participant. ?as=listen is still accepted
    // server-side for backward-compat (legacy share URLs continue to work).
    const inviteCode = roomMeta?.invite_code;
    const url = inviteCode
      ? `${window.location.origin}/join/${inviteCode}?as=participant`
      : `${window.location.origin}/r/${roomId}?as=participant`;
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      try {
        const ta = document.createElement("textarea");
        ta.value = url; ta.style.position = "fixed"; ta.style.opacity = "0";
        document.body.appendChild(ta); ta.select();
        document.execCommand("copy");
        ta.remove(); ok = true;
      } catch { /* */ }
    }
    setCopied(ok);
    setTimeout(() => setCopied(false), 1500);
    if (ok) toast.success("Participant link copied", { description: url, duration: 2500 });
    else toast.error("Couldn't copy. Long-press the room id to copy manually.");
  };

  const handleModerate = async (target_user_id, action) => {
    try {
      const r = await moderate(roomId, { target_user_id, action });
      if (r.room) setRoomMeta(r.room);
    } catch (e) {
      alert(fmtErr(e?.response?.data?.detail, "Moderation failed."));
    }
  };

  const [endModalOpen, setEndModalOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const handleEndRoom = async () => {
    setLeaving(true);
    let closeResult = null;
    try {
      // Use a race with timeout because the LiveKit data broadcast can take time.
      const timeout = new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 12000));
      closeResult = await Promise.race([endRoom(roomId), timeout]);
    } catch (e) {
      // Even on timeout, the DB update likely succeeded. Proceed to ended state.
      if (e?.message !== "timeout") {
        toast.error(fmtErr(e?.response?.data?.detail, "Couldn't end room."));
        setLeaving(false);
        setEndModalOpen(false);
        return;
      }
    }
    // The host does not receive its own data message, so route it directly.
    setEndedInfo({
      ended_at: new Date().toISOString(),
      can_reopen: false,
    });
    setPhase("ended");
    setEndModalOpen(false);
    try { lkRoomRef.current?.disconnect(); } catch { /* */ }
  };

  const handleLeaveRoom = async () => {
    setLeaving(true);
    try { await leaveSession(roomId); } catch { /* */ }
    try { lkRoomRef.current?.disconnect(); } catch { /* */ }
    // Auth users → dashboard; guests / unauth → login chooser
    navigate(user ? "/" : "/login");
  };

  // R18-5 — Audience: request the camera. Server creates a pending row + a
  // camera_decision broadcast follows when staff approves / denies.
  const handleRequestCamera = async () => {
    if (myPendingRequestId || requestingCam) return;
    setRequestingCam(true);
    try {
      const r = await requestCamera(roomId);
      const rid = r?.request?.request_id;
      if (rid) {
        setMyPendingRequestId(rid);
        toast.success(r.already_pending
          ? "You already have a pending request"
          : "Camera request sent — host will review");
      }
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail, "Couldn't send request"));
    } finally {
      setRequestingCam(false);
    }
  };

  // R18-5 — Staff: approve a pending camera request.
  const handleApproveCameraRequest = async ({ request_id }) => {
    setPendingRequests((prev) => prev.filter((r) => r.request_id !== request_id));
    try {
      await approveCameraRequest(roomId, request_id);
      try { const m = await getRoom(roomId); setRoomMeta(m); } catch { /* */ }
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail, "Approve failed"));
      // Re-fetch on failure so the row reappears if the API actually rejected it.
      try {
        const r = await listCameraRequests(roomId);
        setPendingRequests(r?.requests || []);
      } catch { /* */ }
    }
  };

  // R18-5 — Staff: deny a pending camera request.
  const handleDenyCameraRequest = async ({ request_id }) => {
    setPendingRequests((prev) => prev.filter((r) => r.request_id !== request_id));
    try {
      await denyCameraRequest(roomId, request_id);
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail, "Deny failed"));
      try {
        const r = await listCameraRequests(roomId);
        setPendingRequests(r?.requests || []);
      } catch { /* */ }
    }
  };

  // R18-5 — Staff: invite a specific user to camera (no pending request needed).
  const handleInviteCamera = async (targetUserId, targetName) => {
    try {
      await inviteToCamera(roomId, targetUserId);
      toast.success(`${targetName || "User"} invited on cam`);
      try { const m = await getRoom(roomId); setRoomMeta(m); } catch { /* */ }
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail, "Invite failed"));
    }
  };

  // R18-5 — Staff: revoke a user's camera grant.
  const handleRevokeCamera = async (targetUserId, targetName) => {
    try {
      await revokeCameraByUser(roomId, targetUserId);
      toast.message(`${targetName || "Camera"} revoked`);
      try { const m = await getRoom(roomId); setRoomMeta(m); } catch { /* */ }
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail, "Revoke failed"));
    }
  };

  // R18-9 — Host manually extends the room (resets idle clock).
  const handleExtendRoom = async () => {
    if (busyExtending) return;
    setBusyExtending(true);
    try {
      await extendRoom(roomId);
      setIdleBanner(null);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 429) toast.error("Already extended recently — try again in 10 minutes.");
      else toast.error(fmtErr(err?.response?.data?.detail, "Couldn't extend room"));
    } finally {
      setBusyExtending(false);
    }
  };

  // ---------- Render ----------
  // Derive role/DJ state and own the DJ engine BEFORE any conditional return —
  // React Hook rules require unconditional invocation order.
  const isGlobalSuperAdmin = user?.platform_role === "super_admin";
  const isStaff = isGlobalSuperAdmin || appRole === "host" || appRole === "cohost";
  const canEndRoom = isGlobalSuperAdmin || appRole === "host";
  const myIdentity = localIdentity || user?.user_id || null;
  // Room metadata is authoritative for live role changes. Promote or demote
  // the local UI as soon as a cohost or host transfer is broadcast and the
  // refreshed room document arrives.
  useEffect(() => {
    if (!localIdentity || !roomMeta) return;
    let nextRole = "participant";
    if (localIdentity === roomMeta.host_user_id) nextRole = "host";
    else if ((roomMeta.cohost_user_ids || []).includes(localIdentity)) nextRole = "cohost";
    setAppRole((current) => current === "guest" && nextRole === "participant" ? current : nextRole);
  }, [localIdentity, roomMeta]);
  const currentDjUserId = roomMeta?.current_dj_user_id || roomMeta?.host_user_id;
  const isCurrentDj = !!myIdentity && currentDjUserId === myIdentity;
  useEffect(() => {
    if (phase !== "connected" || !isCurrentDj) return;
    // Poll fallback for a lost DJ handoff packet. It keeps the in room DJ
    // board functional for a guest or signed in participant alike.
    refreshRoomToken(roomId).then(async (tokenResult) => {
      if (!tokenResult?.token) return;
      setTokenCanPublish(!!tokenResult.can_publish);
      try { await lkRoomRef.current?.updateToken(tokenResult.token); } catch { /* next refresh retries */ }
    }).catch(() => {});
  }, [phase, isCurrentDj, roomId]);
  const canAssignDj = isGlobalSuperAdmin || (isStaff && (appRole === "host" || !!roomMeta?.cohosts_can_assign_dj));
  // R18-1 — Mode-aware copy. Defaults to club for legacy rooms with no mode.
  const roomMode = (roomMeta?.mode || "club").toLowerCase();
  // Camera request system removed: all participants can publish camera.
  const audienceCanRequestCam = false;
  // R18-5 — Live camera count (host + camera_approved_user_ids) for the
  // staff tray "n/cap" badge.
  const liveCameraCount = (roomMeta?.camera_approved_user_ids || []).length
    + (roomMeta?.host_user_id && !(roomMeta?.camera_approved_user_ids || []).includes(roomMeta.host_user_id) ? 1 : 0);

  // Camera request polling removed: all participants can publish camera.
  // (pendingRequests state kept for compatibility but never populated)

  // R22-MVP — Mic auto-mute. When the DJ is broadcasting audio, all non-DJ
  // participants with mic access (cohosts) are automatically muted to prevent
  // feedback and crosstalk. A toast informs them. When DJ stops, mic is restored.
  const prevDjPlayingRef = useRef(false);
  useEffect(() => {
    const room = lkRoomRef.current;
    if (!room || phase !== "connected" || isCurrentDj) return;
    const playing = djInfo.playing;
    if (playing && !prevDjPlayingRef.current) {
      // DJ just started broadcasting — mute local mic if publishing
      const localP = room.localParticipant;
      if (localP) {
        const micPub = Array.from(localP.audioTrackPublications.values()).find(
          (p) => p.source === Track.Source.Microphone && !p.isMuted
        );
        if (micPub) {
          localP.setMicrophoneEnabled(false);
          toast.info("Your mic was muted while DJ is live.", { duration: 4000 });
        }
      }
    }
    prevDjPlayingRef.current = playing;
  }, [djInfo.playing, phase, isCurrentDj]);

  // R18-4 — Roster poll (every 8s) for the Crowd Wall. Includes both
  // publishing and non-publishing members. The publishing subset is rendered
  // via the existing CamTile pipeline; non-publishers get <CrowdAvatarTile>.
  useEffect(() => {
    if (phase !== "connected" || !roomId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const r = await getRoomRoster(roomId);
        if (!cancelled) setRoster(r?.members || []);
      } catch (err) {
        if (err?.response?.status !== 401 && err?.response?.status !== 403) {
          console.warn("[R18-4] roster fetch failed:", err);
        }
      }
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, roomId]);

  // R18-9 — DJ heartbeat. Frontend posts every 15s WHEN the local user is
  // the current DJ. Backend's track-published webhook is the OR-source.
  useEffect(() => {
    if (phase !== "connected" || !roomId || !isCurrentDj) return undefined;
    let cancelled = false;
    const post = async () => {
      try { await djHeartbeat(roomId); }
      catch (err) {
        if (err?.response?.status !== 403) {
          console.warn("[R18-9] dj heartbeat failed:", err);
        }
      }
    };
    post();
    const id = setInterval(() => { if (!cancelled) post(); }, 15000);
    return () => { cancelled = true; clearInterval(id); };
  }, [phase, roomId, isCurrentDj]);

  // R18-7 — Subscribe-tick poster. Posts the size of subscribedRef every 60s
  // so the backend telemetry ticker can aggregate `active_subscribed_video_count`
  // across all clients. Only counts video subscriptions; the always-on DJ audio
  // is excluded from the egress proxy.
  useEffect(() => {
    if (phase !== "connected" || !roomId) return undefined;
    let cancelled = false;
    const post = async () => {
      try {
        const count = Array.from(subscribedRef.current).filter((k) => k.endsWith(":video")).length;
        await postSubscribeTick(roomId, count, localIdentity || null);
      } catch (err) {
        // Silent — tick is best-effort.
      }
    };
    const id = setInterval(() => { if (!cancelled) post(); }, 60000);
    // First tick after a 5s settle so the IntersectionObserver has time to fire.
    const initial = setTimeout(() => { if (!cancelled) post(); }, 5000);
    return () => { cancelled = true; clearInterval(id); clearTimeout(initial); };
  }, [phase, roomId, localIdentity]);

  // R18-8 — Pin slot stickiness: snapshot display_name / role for every
  // currently-pinned identity so we can render the placeholder later.
  useEffect(() => {
    if (!Array.isArray(pinnedIdentities) || pinnedIdentities.length === 0) return;
    setPinnedMeta((prev) => {
      const next = { ...prev };
      for (const ident of pinnedIdentities) {
        const live = participants.find((p) => p.identity === ident);
        if (live) {
          next[ident] = {
            display_name: live.name || live.display_name || "Member",
            role: live.identity === roomMeta?.host_user_id ? "host"
                  : ((roomMeta?.cohost_user_ids || []).includes(live.identity) ? "cohost" : "audience"),
            is_dj: live.identity === (roomMeta?.current_dj_user_id || roomMeta?.host_user_id),
          };
        }
      }
      // Drop entries that are no longer pinned.
      for (const ident of Object.keys(next)) {
        if (!pinnedIdentities.includes(ident)) delete next[ident];
      }
      return next;
    });
  }, [pinnedIdentities, participants, roomMeta?.host_user_id, roomMeta?.current_dj_user_id, roomMeta?.cohost_user_ids]);

  // R16-D — Multi-pin gallery hooks. Placed AFTER myIdentity is computed so
  // the persistence/auto-pin effects can safely read it (TDZ-safe). All
  // hooks above run unconditionally before this on every render — order is
  // stable.
  useEffect(() => {
    if (!roomId || !myIdentity) return;
    try {
      const raw = localStorage.getItem(`sr-pinned-${roomId}-${myIdentity}`);
      const parsed = raw ? JSON.parse(raw) : [];
      if (Array.isArray(parsed)) setPinnedIdentities(parsed.slice(0, 15));
    } catch { /* */ }
    try {
      const v = localStorage.getItem(`sr-autopin-on-deck-${myIdentity}`);
      if (v === "0") setAutoPinOnDeck(false);
      if (v === "1") setAutoPinOnDeck(true);
    } catch { /* */ }
  }, [roomId, myIdentity]);

  useEffect(() => {
    if (!roomId || !myIdentity) return;
    try {
      localStorage.setItem(
        `sr-pinned-${roomId}-${myIdentity}`,
        JSON.stringify(pinnedIdentities),
      );
    } catch { /* */ }
  }, [pinnedIdentities, roomId, myIdentity]);

  useEffect(() => {
    if (!myIdentity || !currentDjUserId) return;
    const amDj = currentDjUserId === myIdentity;
    if (amDj && !wasDjRef.current) {
      applyViewMode("DJ_ONLY");
      if (autoPinOnDeck) {
        setPinnedIdentities((prev) => {
          if (prev.includes(myIdentity)) return prev;
          return [myIdentity, ...prev.filter((id) => id !== myIdentity)].slice(0, 15);
        });
      }
    }
    wasDjRef.current = amDj;
  }, [currentDjUserId, myIdentity, autoPinOnDeck, applyViewMode]);

  const togglePin = useCallback((identity) => {
    setPinnedIdentities((prev) => {
      if (prev.includes(identity)) return prev.filter((id) => id !== identity);
      if (prev.length >= 15) {
        try {
          window.dispatchEvent(new CustomEvent("sr-toast", {
            detail: { kind: "warn", msg: "Max 15 pins \u2014 unpin one first." },
          }));
        } catch { /* */ }
        return prev;
      }
      return [...prev, identity];
    });
  }, []);

  const clearPins = useCallback(() => setPinnedIdentities([]), []);

  const setAutoPin = useCallback((on) => {
    setAutoPinOnDeck(on);
    try { localStorage.setItem(`sr-autopin-on-deck-${myIdentity}`, on ? "1" : "0"); } catch { /* */ }
  }, [myIdentity]);
  void setAutoPin; // R16-D — exposed via settings panel in a follow-up

  // Phase 3.6 — DJ engine owned by the main room tab. The optional pop-out
  // window remote-controls this engine via BroadcastChannel. Engine stays
  // here regardless of UI location, so LiveKit broadcast survives pop-out
  // open/close.
  const { engineRef: djEngineRef, popoutAlive: djPopoutAlive, popoutEverOpened: djPopoutEverOpened, openPopout: openDjPopout } = useDjEngine({
    room: lkRoomRef.current,
    roomId,
    enabled: isCurrentDj && phase === "connected",
    onNowPlaying: (deck, title) => setDjInfo((s) => ({ ...s, name: user?.display_name || s.name, track: `Deck ${deck}: ${title}`, playing: true })),
  });

  const openChatPopout = useCallback(() => {
    const win = window.open(`/r/${roomId}/chat-popout`, `chat-popout-${roomId}`, "width=420,height=720,resizable=yes");
    if (win) {
      setChatOpen(false);
      setChatMinimized(false);
      win.focus?.();
    } else {
      toast.error("Your browser blocked the chat window. Please allow popups for Full Spectrum Center.");
    }
  }, [roomId]);

  // Phase 3.6 R2 — Share Audio (system audio) lives in the main tab as a
  // separate LiveKit publish track. Toggled from the bottom dock.
  // Phase A R6 B8 — pass engineRef so the hook can re-resume the DJ
  // engine's AudioContext after getDisplayMedia (some Chrome configs
  // suspend the ctx during tab-capture handshake which silences the deck
  // on the host's machine).
  const { on: shareAudioOn, toggle: toggleShareAudio, lastError: shareAudioErr } = useShareAudio({
    room: lkRoomRef.current,
    enabled: phase === "connected",
    engineRef: djEngineRef,
  });
  useEffect(() => {
    if (shareAudioErr) toast.error(`Share Audio: ${shareAudioErr.message || shareAudioErr}`);
  }, [shareAudioErr]);
  // Phase A R6 B9 — pre-share confirmation modal. Surfaced before
  // getDisplayMedia so DJs understand the OS picker is coming + that
  // Full Spectrum Center never receives screen content. Dismissable persistently
  // via the "Don't show again" checkbox.
  const [shareAudioPreOpen, setShareAudioPreOpen] = useState(false);
  const handleToggleShareAudio = () => {
    if (shareAudioOn) {
      // Going OFF — no modal needed, just stop sharing.
      toggleShareAudio();
      return;
    }
    // Going ON — check if the user has dismissed the modal previously.
    if (shareAudioModalDismissed()) {
      toggleShareAudio();
      return;
    }
    setShareAudioPreOpen(true);
  };

  if (phase === "guest-door") {
    return (
      <GuestDoor
        roomId={roomId}
        onAdmitted={async () => {
          // The guest cookie is now set. Do not advance to the device preview
          // until token issuance succeeds; otherwise Enter Room would show the
          // internal "no pending token" error to a valid returning guest.
          try {
            const tok = await getRoomToken(roomId);
            if (tok?.ok && tok.token) {
              setTokenDisplayName(tok.display_name || "");
              setAppRole(tok.app_role || "guest");
              if (tok.room_id) setRoomMeta((prev) => ({ ...prev, ...tok }));
              await triggerConnect(tok);
              return;
            }
            if (tok?.kind === "waiting" || tok?.status === "waiting") {
              setWaitingPos(tok.position || 0);
              setPhase("waiting");
              return;
            }
            if (tok?.kind === "ended" || tok?.kind === "closed") {
              setEndedInfo({ ended_at: tok.ended_at || new Date().toISOString(), can_reopen: false });
              setErrMsg(tok.error?.message || "This event is closed.");
              setPhase("ended");
              return;
            }
            if (tok?.kind === "expired") {
              setPhase("expired");
              return;
            }
            if (tok?.kind === "already_in_room") {
              setConflict({ current_room_id: tok.error?.current_room_id, current_room_name: tok.error?.current_room_name });
              setPhase("conflict");
              return;
            }
            if (tok?.kind === "forbidden") {
              setPhase("guest-banned");
              return;
            }
            // A stale cookie from another room is recoverable: return to the
            // guest door, which mints a fresh room-scoped guest cookie.
            if (tok?.kind === "guest_token_wrong_room") {
              setPhase("guest-door");
              return;
            }
            setPhase("error");
            setErrMsg(tok?.error?.message || "We could not complete room entry. Please try again.");
          } catch (e) {
            console.warn("[guest-door] token fetch after admission failed:", e);
            setPhase("error");
            setErrMsg("We could not complete room entry. Please try again.");
          }
        }}
      />
    );
  }
  if (phase === "guest-banned") {
    return (
      <GuestDoor roomId={roomId} initialBanned={true} />
    );
  }

  if (phase === "preview") {
    // R13-C — Pre-Room Preview gate. We have a valid token in pendingTokenRef
    // but haven't called connectLiveKit() yet. User picks devices + display
    // name in PreRoomPreview; on Enter Room we resume the connect chain.
    return (
      <PreRoomPreview
        mode="entry"
        roomName={roomMeta?.name || "Full Spectrum Center"}
        initialDisplayName={tokenDisplayName || user?.display_name || ""}
        onEnter={async ({ camId, micId, speakerId, displayName }) => {
          try {
            await setMyRoomDisplayName(roomId, displayName);
          } catch (err) {
            toast.error(fmtErr(err?.response?.data?.detail, "Couldn't save your room name."));
            return;
          }
          chosenDevicesRef.current = { camId, micId, speakerId, displayName };
          // eslint-disable-next-line no-console
          console.log(`[pre-room] entering with cam=${camId} mic=${micId} speaker=${speakerId} name=${displayName}`);
          setPhase("connecting");
          // Defer to next tick so React commits the phase transition before
          // LiveKit starts. Without this the loading spinner gets clobbered.
          setTimeout(() => {
            const tok = pendingTokenRef.current;
            const fn  = connectLiveKitRef.current;
            if (tok && fn) {
              fn(tok).catch((err) => {
                // eslint-disable-next-line no-console
                console.error("[pre-room] connectLiveKit failed:", err);
                setPhase("error");
                setErrMsg(err.message || String(err));
              });
            } else {
              setPhase("error");
              setErrMsg("Internal: no pending token");
            }
          }, 50);
        }}
      />
    );
  }

  if (phase === "audience_join") {
    // R18-Phase1 follow-up — Audience listener join card for Lounge/Club.
    // No getUserMedia is called here, so users without a camera (or who
    // declined the prompt) can join without friction. The Request Camera
    // CTA inside the room handles the camera grant flow at the moment the
    // host approves.
    const tokPending = pendingTokenRef.current || {};
    return (
      <AudienceJoinCard
        roomName={roomMeta?.name || "Full Spectrum Center"}
        roomMode={tokPending.mode || roomMeta?.mode || "club"}
        djName={roomMeta?.current_dj_display_name || null}
        initialDisplayName={tokenDisplayName || user?.display_name || tokPending.display_name || ""}
        onEnter={async ({ displayName }) => {
          try {
            await setMyRoomDisplayName(roomId, displayName);
          } catch (err) {
            toast.error(fmtErr(err?.response?.data?.detail, "Couldn't save your room name."));
            return;
          }
          // Stash the chosen display name in the same ref the preview path uses.
          chosenDevicesRef.current = { camId: null, micId: null, speakerId: null, displayName };
          setPhase("connecting");
          setTimeout(() => {
            const tok = pendingTokenRef.current;
            const fn  = connectLiveKitRef.current;
            if (tok && fn) {
              fn(tok).catch((err) => {
                // eslint-disable-next-line no-console
                console.error("[audience-join] connectLiveKit failed:", err);
                setPhase("error");
                setErrMsg(err.message || String(err));
              });
            } else {
              setPhase("error");
              setErrMsg("Internal: no pending token");
            }
          }, 50);
        }}
      />
    );
  }

  if (phase === "waiting") {
    // R13-A2 — waiting room has been removed. Any legacy "waiting" status
    // from the backend is now treated as a soft re-bounce: the server will
    // either auto-approve us on the next /token poll or 410/403 explicitly.
    // We show a brief transitional state and re-fetch immediately.
    return (
      <div className="min-h-screen bg-[#050508] text-white grid place-items-center p-6 text-center"
           data-testid="waiting-room-removed">
        <div className="max-w-sm">
          <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-2">R13 update</div>
          <h1 className="text-lg font-bold mb-2">Waiting room retired</h1>
          <p className="text-sm text-white/65">
            Full Spectrum Center no longer uses a waiting room. Reload to enter directly, or ask the host to unlock the room.
          </p>
          <button
            className="mt-4 px-5 py-2 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] font-bold text-xs uppercase tracking-[0.14em]"
            onClick={() => window.location.reload()}
            data-testid="waiting-room-removed-rejoin"
          >
            Rejoin
          </button>
        </div>
      </div>
    );
  }

  // Only the Center dashboard availability control can open a new event.
  if (phase === "ended" || endedInfo) {
    return <EndedScreen
      roomName={roomMeta?.name || "Room"}
      endedAt={endedInfo?.ended_at || roomMeta?.ended_at}
      canReopen={false}
      onLeave={() => navigate("/")}
    />;
  }
  if (phase === "banned") {
    return <Notice
      icon={<Ban className="w-7 h-7 text-[#FF007F]" />}
      title="Removed from this room"
      message={errMsg || "You've been removed from this room until the host resets it."}
      onBack={() => navigate("/")}
      backLabel="Go home"
      testid="banned-screen"
    />;
  }
  if (phase === "expired") {
    return <Notice
      icon={<Clock className="w-7 h-7 text-amber-400" />}
      title="Room ended"
      message={errMsg || "This room is over."}
      onBack={() => navigate("/")}
      backLabel="Go home"
      testid="expired-screen"
    />;
  }
  if (phase === "room_full") {
    // R18-10 — surfaced when /token returns 410 room_full.
    return <RoomFullCard
      cap={roomFullCap}
      retrying={roomFullRetrying}
      onBack={() => navigate(user ? "/" : "/login")}
      onRetry={async () => {
        setRoomFullRetrying(true);
        try {
          const t = await getRoomToken(roomId);
          if (t.ok) {
            // Connect directly with the fresh token. Reloading here recreated
            // the entrance flow and made a room-full retry feel like a new visit.
            setAppRole(t.app_role);
            setTokenCanPublish(!!t.can_publish);
            if (t.display_name) setTokenDisplayName(t.display_name);
            await connectLiveKitRef.current?.(t);
            return;
          }
          if (t.kind === "room_full") {
            setRoomFullCap(typeof t.cap === "number" ? t.cap : null);
          } else {
            // Some other failure — bubble up.
            setPhase("error");
            setErrMsg(t.error?.message || "Couldn't issue token.");
          }
        } catch (err) {
          console.error("[R18-10] retry failed:", err);
        } finally {
          setRoomFullRetrying(false);
        }
      }}
    />;
  }
  if (phase === "error") {
    return <Notice
      icon={<AlertTriangle className="w-7 h-7 text-[#FF007F]" />}
      title="Couldn't join the room"
      message={errMsg || "Unknown error"}
      onBack={() => navigate("/")}
      backLabel="Go home"
      testid="room-error"
    />;
  }

  const isStaff_DUPE_REMOVED = appRole === "host" || appRole === "cohost";
  // Phase 2.2 — DJ console mount gate is decoupled from host status.
  // Mounts ONLY when the local participant IS the current DJ (works for guests too).
  // Prefer the live LiveKit identity (set on RoomEvent.Connected) over the auth
  // user_id — they should match for authed users, but for guests `user` is
  // `false`, and for races where the page renders before connect, we need to
  // fall back consistently. NEVER read `lkRoomRef.current.localParticipant`
  // here — refs don't trigger re-renders, so the gate would stay stale.
  // (isStaff / myIdentity / currentDjUserId / isCurrentDj / canAssignDj were
  // lifted above the early returns so the DJ-engine hook can run unconditionally.)

  // Phase 3.6 — DJ engine call moved above early returns; see top of render.
  void isStaff_DUPE_REMOVED;

  // Phase 2.2.x — manual escape hatch.
  const handleResync = async () => {
    setResyncing(true);
    try {
      const [meta] = await Promise.all([
        getRoom(roomId).catch(() => null),
        getRoomToken(roomId).catch(() => null),
      ]);
      if (meta) setRoomMeta(meta);
      const id = lkRoomRef.current?.localParticipant?.identity;
      if (id) setLocalIdentity(id);
      // eslint-disable-next-line no-console
      console.log("[SR] resync ->", {
        myIdentity: id, currentDjUserId: meta?.current_dj_user_id, hostId: meta?.host_user_id,
      });
      toast.success(`Resynced · dj=${(meta?.current_dj_user_id || "").slice(0,10)}`);
    } catch (e) {
      toast.error("Resync failed: " + (e?.message || "unknown"));
    } finally {
      setResyncing(false);
    }
  };

  // ===== Phase 2.3 — host room control handlers =====
  const handleToggleLock = async () => {
    const next = !roomMeta?.locked;
    try {
      await lockRoomApi(roomId, next);
      await getRoom(roomId).then(setRoomMeta).catch(() => {});
      toast.success(next ? "Room locked · new joins blocked" : "Room unlocked");
    } catch (e) { toast.error(fmtErr(e?.response?.data?.detail, "Lock failed")); }
  };
  const handleToggleAlwaysCamera = async (enabled) => {
    try {
      await setAlwaysCameraOn(roomId, enabled);
      setRoomMeta((prev) => prev ? { ...prev, always_camera_on: enabled } : prev);
      toast.success(enabled ? "Always-start cameras enabled for new joins" : "Cameras are optional for new joins");
    } catch (e) { toast.error(fmtErr(e?.response?.data?.detail, "Camera policy update failed")); }
  };
  const handleBeginClosing = async () => {
    try {
      await startClosing(roomId);
      await getRoom(roomId).then(setRoomMeta).catch(() => {});
      toast.success("Closing in 60s · everyone will see the countdown");
    } catch (e) { toast.error(fmtErr(e?.response?.data?.detail, "Begin closing failed")); }
  };
  const handleCancelClosing = async () => {
    try {
      await cancelClosing(roomId);
      await getRoom(roomId).then(setRoomMeta).catch(() => {});
      toast.success("Closing cancelled");
    } catch (e) { toast.error(fmtErr(e?.response?.data?.detail, "Cancel failed")); }
  };
  const handleEndNow = async () => {
    try {
      await endRoom(roomId);
      // The room_ended data message + 410 on poll will route us into the
      // EndedScreen, so we don't need to setPhase here.
    } catch (e) { toast.error(fmtErr(e?.response?.data?.detail, "End room failed")); }
  };
  const handleOpenRename = (identity, currentName) => setRenameTarget({ identity, name: currentName });
  const handleConfirmRename = async (targetId, newName) => {
    try {
      await renameApi(roomId, targetId, newName);
      await getRoom(roomId).then(setRoomMeta).catch(() => {});
      setRenameTarget(null);
      toast.success(`Renamed to ${newName}`);
    } catch (e) { toast.error(fmtErr(e?.response?.data?.detail, "Rename failed")); }
  };


  return (
    <div className={`${isCurrentDj ? "h-screen overflow-hidden flex flex-col" : "min-h-screen"} bg-[#050508] text-white relative`}>
      <RoomContentGuard enabled={phase === "connected"} username={tokenDisplayName || user?.display_name} />
      {/* R22-MVP — Waiting Room overlay */}
      {inWaitingRoom && (
        <WaitingRoom
          reason={waitingRoomReason}
          onReturn={async ({ turnstileToken }) => {
            try {
              await returnFromWaitingRoom(roomId, turnstileToken);
              setInWaitingRoom(false);
              setWaitingRoomReason(null);
              camOffCountRef.current = 0;
              toast.success("Welcome back! You've returned to the room.");
            } catch (e) {
              toast.error(fmtErr(e?.response?.data?.detail, "Failed to return from waiting room"));
            }
          }}
        />
      )}
      <ClosingBanner closingAt={roomMeta?.closing_at} hostName={roomMeta?.host_name} />
      <RenameModal
        open={!!renameTarget}
        currentName={renameTarget?.name}
        targetId={renameTarget?.identity}
        onClose={() => setRenameTarget(null)}
        onConfirm={handleConfirmRename}
      />
      <audio ref={djAudioElRef} className="hidden" preload="auto" />
      <audio ref={remoteDjAudioRef} className="hidden" autoPlay />

      {/* Phase 2.2 — single-active-room conflict modal */}
      {conflict && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/80 backdrop-blur px-4" data-testid="conflict-modal">
          <div className="max-w-md w-full sr-glass rounded-2xl p-6 border border-amber-500/40">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <h3 className="font-bold">You're already in another room</h3>
            </div>
            <p className="text-sm text-white/70 mb-1">
              You can only be in one room at a time. You're currently in
              <span className="font-semibold text-white mx-1.5 px-2 py-0.5 rounded bg-white/10" data-testid="conflict-current-room-name">{conflict.current_room_name}</span>.
            </p>
            <p className="text-xs text-white/40 mb-4">Switching will end your session in that room and bring you here.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setConflict(null); navigate("/"); }} disabled={switching}
                className="px-3 py-2 text-xs font-bold bg-white/10 hover:bg-white/20 rounded-md"
                data-testid="conflict-cancel-btn">
                Cancel
              </button>
              <button onClick={onSwitchRooms} disabled={switching}
                className="px-3 py-2 text-xs font-bold bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] rounded-md disabled:opacity-50"
                data-testid="leave-previous-room-btn">
                {switching ? "Leaving…" : "Leave previous room & join here"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Phase: End Room confirmation modal (host only) - offers Begin Closing (60s countdown) or End Now */}
      {endModalOpen && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/80 backdrop-blur px-4" data-testid="end-room-modal">
          <div className="max-w-md w-full sr-glass rounded-2xl p-6 border border-[#E53935]/40">
            <div className="flex items-center gap-2 mb-3">
              <OctagonAlert className="w-5 h-5 text-[#FF8A85]" />
              <h3 className="font-bold">End Meeting</h3>
            </div>
            <p className="text-sm text-white/70 mb-4">
              Choose how to end this room. "Begin End Meeting" gives everyone a 60-second warning countdown before closing. "End Now" disconnects all participants immediately.
            </p>
            <div className="flex justify-end gap-2 flex-wrap">
              <button onClick={() => setEndModalOpen(false)} disabled={leaving}
                className="px-3 py-2 text-xs font-bold bg-white/10 hover:bg-white/20 rounded-md"
                data-testid="end-room-cancel-btn">Cancel</button>
              <button onClick={async () => { setEndModalOpen(false); await handleBeginClosing(); }} disabled={leaving || !!roomMeta?.closing_at}
                className="px-3 py-2 text-xs font-bold bg-amber-600 hover:bg-amber-500 text-white rounded-md disabled:opacity-50"
                data-testid="end-room-begin-closing-btn">
                Begin End Meeting (60s)
              </button>
              <button onClick={handleEndRoom} disabled={leaving}
                className="px-3 py-2 text-xs font-bold bg-[#E53935] hover:bg-[#FF5555] text-white rounded-md disabled:opacity-50"
                data-testid="end-room-confirm-btn">
                {leaving ? "Ending\u2026" : "End Now"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header — Phase 3.6 dedupe pass.
          The bottom dock is now the single source of truth for room controls.
          The header keeps ONLY: room name, meeting timer, green shield,
          copy link, view-mode toggle (DJ only), and the Dev toggle
          (which itself is gated behind localStorage `sr-dev-mode=1`). */}
      <header className="sticky top-0 z-40 sr-glass" data-testid="room-header">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-3 sm:gap-4">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center shrink-0">
            <Disc3 className="w-4 h-4 text-white" style={djInfo.playing ? { animation: "spin 4s linear infinite" } : {}} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40" data-testid="room-name-label">Room</div>
            <div className="font-display text-base sm:text-lg font-bold truncate" data-testid="room-name-display" title={roomMeta?.name || ""}>
              {roomMeta?.name || "—"}
            </div>
          </div>

          <div className="lg:hidden shrink-0">
            <MeetingTimer startedAt={roomMeta?.current_event_started_at} />
          </div>

          {/* Phase A R11 C — collapse inline pills under `<lg` (1024px)
              into a single hamburger overflow. The hamburger is rendered
              with `lg:hidden`, the inline pill group with `hidden lg:flex`.
              Pills inside the overflow are stacked vertically. */}
          <div className="hidden lg:flex items-center gap-3 sm:gap-4 flex-wrap">
            {/* Meeting timer + green shield — visible to everyone */}
            <MeetingTimer startedAt={roomMeta?.current_event_started_at} />
            <SecurityShield />

            {/* Copy shareable room link */}
            <button onClick={copyRoomId}
                    className="flex items-center gap-2 px-2.5 sm:px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-[11px] sm:text-xs font-mono transition-colors"
                    data-testid="copy-room-id-btn" title="Copy shareable room link">
              <span className="hidden sm:inline text-white/40">room</span>
              <span className="text-white/90">{roomId}</span>
              {copied ? <Check className="w-3.5 h-3.5 text-[#00F0FF]" /> : <Copy className="w-3.5 h-3.5 text-white/50" />}
            </button>

            {/* View-mode toggle — DJ only */}
            {isCurrentDj && (
              <div className="hidden md:flex items-center gap-0.5 p-0.5 bg-white/5 border border-white/10 rounded-lg" data-testid="view-mode-toggle">
                {[
                  { key: "DJ_ONLY",   label: "DJ Only",   Icon: Sliders,    testid: "viewmode-dj" },
                  // { key: "SPLIT",     label: "Split",     Icon: Columns,    testid: "viewmode-split" }, // Hidden per user request
                  { key: "ROOM_ONLY", label: "Room Only", Icon: LayoutGrid, testid: "viewmode-room" },
                ].map(({ key, label, Icon, testid }) => {
                  const active = viewMode === key;
                  return (
                    <button key={key} onClick={() => applyViewMode(key)}
                      className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-bold uppercase tracking-[0.12em] transition-colors ${
                        active ? "bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white shadow-[0_0_14px_-2px_rgba(255,0,127,0.4)]" : "text-white/60 hover:text-white hover:bg-white/5"
                      }`}
                      data-testid={testid} title={label}>
                      <Icon className="w-3.5 h-3.5" />
                      <span className="hidden lg:inline">{label}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Dev toggle — only rendered when sr-dev-mode=1 is set in localStorage.
                Toggling clears the flag and hides itself; re-enable via DevTools. */}
            {devMode && (
              <>
                <DiagPill
                  myIdentity={myIdentity}
                  currentDjUserId={currentDjUserId}
                  hostId={roomMeta?.host_user_id}
                  cohostCount={(roomMeta?.cohost_user_ids || []).length}
                  recentDataMsgs={recentDataMsgs}
                  expanded={diagExpanded}
                  onToggle={() => setDiagExpanded((v) => !v)}
                />
                <button onClick={toggleDevMode}
                        className="hidden sm:inline-flex items-center gap-1 px-2 py-1.5 rounded-lg border text-[10px] uppercase tracking-[0.2em] font-bold transition-colors bg-[#FF007F]/15 border-[#FF007F]/40 text-[#FF007F]"
                        data-testid="dev-mode-toggle"
                        title="Dev mode ON — click to hide diagnostics. Re-enable via localStorage sr-dev-mode=1.">
                  🔧 Dev
                </button>
              </>
            )}
          </div>

          {/* Hamburger overflow for `<lg`. Opens a dropdown anchored to the
              top-right edge with all the same controls as the inline group,
              vertically stacked with comfortable touch targets. */}
          <div className="relative lg:hidden ml-auto">
            <button
              onClick={() => setHeaderMenuOpen((v) => !v)}
              className="p-2 rounded-lg bg-white/10 hover:bg-white/20 border border-white/15 min-w-[44px] min-h-[44px] flex items-center justify-center"
              data-testid="header-overflow-menu"
              aria-label="Open header menu"
              title="Show all room controls">
              <Menu className="w-5 h-5 text-white" />
            </button>
            {headerMenuOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setHeaderMenuOpen(false)} data-testid="header-overflow-backdrop" />
                <div className="absolute right-0 top-full mt-2 z-50 w-[280px] max-w-[92vw] bg-[#08080c] border border-white/15 rounded-lg shadow-2xl p-3 flex flex-col gap-2"
                     data-testid="header-overflow-menu-panel">
                  <MeetingTimer startedAt={roomMeta?.current_event_started_at} />
                  <SecurityShield />
                  <button onClick={() => { copyRoomId(); setHeaderMenuOpen(false); }}
                          className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-xs font-mono"
                          data-testid="copy-room-id-btn-overflow">
                    <span className="text-white/40">room</span>
                    <span className="text-white/90 flex-1 text-left">{roomId}</span>
                    {copied ? <Check className="w-4 h-4 text-[#00F0FF]" /> : <Copy className="w-4 h-4 text-white/50" />}
                  </button>
                  {isCurrentDj && (
                    <div className="flex flex-col gap-1 p-1 bg-white/5 border border-white/10 rounded-lg" data-testid="view-mode-toggle-overflow">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 px-2 pt-1">View mode</div>
                      {[
                        { key: "DJ_ONLY",   label: "DJ Only",   Icon: Sliders,    testid: "viewmode-dj-overflow" },
                        // { key: "SPLIT",     label: "Split",     Icon: Columns,    testid: "viewmode-split-overflow" }, // Hidden per user request
                        { key: "ROOM_ONLY", label: "Room Only", Icon: LayoutGrid, testid: "viewmode-room-overflow" },
                      ].map(({ key, label, Icon, testid }) => {
                        const active = viewMode === key;
                        return (
                          <button key={key} onClick={() => { applyViewMode(key); setHeaderMenuOpen(false); }}
                            className={`flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded-md text-xs font-bold uppercase tracking-[0.12em] ${
                              active ? "bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white" : "text-white/70 hover:text-white hover:bg-white/5"
                            }`}
                            data-testid={testid}>
                            <Icon className="w-4 h-4" /> {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {devMode && (
                    <button onClick={() => { toggleDevMode(); setHeaderMenuOpen(false); }}
                            className="flex items-center gap-2 px-3 py-2.5 min-h-[44px] rounded-lg border text-xs uppercase tracking-[0.16em] font-bold bg-[#FF007F]/15 border-[#FF007F]/40 text-[#FF007F]"
                            data-testid="dev-mode-toggle-overflow">
                      🔧 Dev mode (on — tap to hide)
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {phase === "connecting" && (
        <div className="fixed inset-0 z-30 grid place-items-center bg-[#050508]/80 backdrop-blur-sm" data-testid="connecting-overlay">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF] mx-auto mb-3" />
            <div className="text-sm uppercase tracking-[0.25em] text-white/60">Connecting to room…</div>
          </div>
        </div>
      )}

      {/* Gallery JSX is rendered inline below; we extract its content via an IIFE so
          we can place it either as the whole body (non-host) or as the right pane (host). */}
      {(() => {
        const galleryBody = (
          <>
            <div className="flex items-center justify-between mb-4 gap-3">
              <div className="text-xs uppercase tracking-[0.25em] text-white/40 flex items-center gap-2">
                <Users className="w-3.5 h-3.5" />
                {sortedParticipants.length} {sortedParticipants.length === 1 ? "person" : "people"} in the room
              </div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase tracking-[0.2em] text-white/30 hidden sm:inline">Sort</label>
                <div className="min-w-[180px]">
                  <DarkSelect
                    value={galleryView}
                    onChange={(v) => setGallerySort(v)}
                    options={[
                      { value: "default", label: "DJ first, then join order" },
                      { value: "first_az", label: "First name A → Z" },
                      { value: "first_za", label: "First name Z → A" },
                      { value: "first_in", label: "First in (oldest)" },
                      { value: "last_in", label: "Last in (newest)" },
                      { value: "role", label: "Role priority (DJ → Host → Cohost → Participant)" },
                    ]}
                    testid="gallery-sort-select"
                  />
                </div>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center gap-3" data-testid="pagination-controls">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={pageSafe === 0} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="pagination-prev" aria-label="Previous page">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="text-xs font-mono text-white/60" data-testid="pagination-indicator">{pageSafe + 1} / {totalPages}</div>
                  <button onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={pageSafe >= totalPages - 1} className="p-2 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="pagination-next" aria-label="Next page">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>

            {/* Zoom-like Video Grid: live low-res video for all participants
                with Speaker/Gallery/Multiple view modes and up to 15 pins.
                Handles pinned/unpinned separation internally. No duplicate CamTile grid. */}
            <VideoGrid
              participants={participants.map((p) => ({ ...p, isCurrentDj: p.identity === currentDjUserId }))}
              pinnedIdentities={pinnedIdentities}
              onTogglePin={togglePin}
              maxPins={15}
              activeSpeaker={activeSpeakerIdentity}
              viewMode={gridViewMode}
              onViewModeChange={(mode) => {
                setGridViewMode(mode);
                try { localStorage.setItem(`sr-grid-view-${roomId}`, mode); } catch { /* */ }
              }}
              myIdentity={myIdentity}
              subscribeIntent={subscribeIntent}
              roomMeta={roomMeta}
              currentDjUserId={currentDjUserId}
            />
          </>
        );

        if (isCurrentDj) {
          // Split layout. DjConsole2 stays mounted in all modes so the audio engine keeps running.
          // When the DJ Board is in its own window, the room tab becomes a
          // full width gallery rather than leaving an empty DJ placeholder.
          const consoleDetached = djPopoutAlive;
          const showConsole = viewMode !== "ROOM_ONLY" && !consoleDetached;
          const showGallery = viewMode !== "DJ_ONLY" || consoleDetached;
          const leftWidth = consoleDetached ? "0%" : viewMode === "DJ_ONLY" ? "100%"
            : viewMode === "ROOM_ONLY" ? "0%"
            : `${splitPct}%`;
          const rightWidth = consoleDetached ? "100%" : viewMode === "ROOM_ONLY" ? "100%"
            : viewMode === "DJ_ONLY" ? "0%"
            : `${100 - splitPct}%`;

          return (
            <div ref={splitContainerRef} className="flex-1 min-h-0 flex w-full" data-testid="host-split-layout">
              {/* Left pane (DJ console). Always mounted; hidden via display:none in ROOM_ONLY so audio engine keeps running. */}
              <div
                className="overflow-hidden h-full"
                style={{ width: leftWidth, display: showConsole ? "block" : "none" }}
                data-testid="host-console-pane">
                {/* The main room is the reliable DJ workspace. The optional
                    separate window remains available from the bottom dock, but
                    no browser popup setting can hide the actual DJ controls. */}
                <div className="h-full w-full relative">
                  <DjConsole2
                    room={lkRoomRef.current}
                    externalEngine={djEngineRef}
                    onNowPlaying={(deck, title) => setDjInfo((s) => ({ ...s, name: user?.display_name || s.name, track: `Deck ${deck}: ${title}`, playing: true }))}
                    layout="inline"
                  />
                </div>
              </div>

              {/* Vertical divider — only draggable in SPLIT mode */}
              {viewMode === "SPLIT" && (
                <div
                  onMouseDown={onSplitDown}
                  onTouchStart={onSplitDown}
                  className="w-1.5 cursor-col-resize bg-white/10 hover:bg-[#00F0FF]/50 active:bg-[#00F0FF] transition-colors flex-shrink-0 relative group"
                  data-testid="split-divider"
                  title="Drag to resize · snaps at 30/50/70%">
                  <div className="absolute inset-y-0 -inset-x-1.5 group-hover:bg-[#00F0FF]/10" />
                  <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-10 rounded-full bg-white/30 group-hover:bg-white pointer-events-none" />
                </div>
              )}

              {/* Right pane (audience gallery) */}
              <div
                className={`overflow-auto h-full ${showMod ? "lg:pr-[26rem]" : ""}`}
                style={{ width: rightWidth, display: showGallery ? "block" : "none" }}
                data-testid="host-gallery-pane">
                <div className="px-4 sm:px-6 py-4">
                  {galleryBody}
                </div>
              </div>
            </div>
          );
        }

        // Non-host (participant / cohost / guest): keep the old simple full-width gallery, no DJ console.
        return (
          <main className={`max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-6 ${showMod ? "lg:pr-[26rem]" : ""}`}>
            {galleryBody}
          </main>
        );
      })()}

      {/* Moderation drawer */}
      {showMod && isStaff && roomMeta && (
        <ModerationDrawer
          room={roomMeta}
          appRole={appRole}
          onClose={() => setShowMod(false)}
          onAction={handleModerate}
          onEndRoom={canEndRoom ? handleEndRoom : null}
          participants={participants}
          currentUserId={user.user_id}
        />
      )}

      {/* Phase 3 — Chat panel overlay (right side, slides in) */}
      {phase === "connected" && (
        <div className={`fixed top-0 right-0 h-screen z-40 transition-transform duration-200 ${chatOpen && !chatMinimized ? "translate-x-0" : "translate-x-full"}`}
             data-testid="chat-panel-wrapper">
          <ChatPanel
            ref={chatPanelRef}
            roomId={roomId}
            myIdentity={myIdentity}
            canModerate={isStaff}
            registerHandler={registerChatHandler}
            participants={participants.map((p) => ({ identity: p.identity, name: p.name }))}
            open={chatOpen && !chatMinimized}
              onClose={() => { setChatOpen(false); setChatMinimized(false); }}
              onPopout={openChatPopout}
            />
        </div>
      )}

      {/* Minimized chat floating badge */}
      {phase === "connected" && chatOpen && chatMinimized && (
        <button
          onClick={() => { setChatMinimized(false); setChatUnread(0); }}
          className="fixed bottom-20 right-4 z-50 w-12 h-12 rounded-full bg-[#6c63ff] hover:bg-[#7c74ff] shadow-lg flex items-center justify-center transition-transform hover:scale-110"
          title="Restore chat"
          data-testid="chat-minimized-badge"
        >
          <MessageCircle className="w-5 h-5 text-white" />
          {chatUnread > 0 && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
              {chatUnread > 99 ? "99+" : chatUnread}
            </span>
          )}
        </button>
      )}

      {/* Phase 3.1 — Participants panel (mutually exclusive with chat panel) */}
      {phase === "connected" && (
        <div className={`fixed top-0 right-0 h-screen z-40 transition-transform duration-200 ${participantsOpen ? "translate-x-0" : "translate-x-full"}`}
             data-testid="participants-panel-wrapper">
          <ParticipantsPanel
            open={participantsOpen}
            onClose={() => setParticipantsOpen(false)}
            participants={participants.map((p) => {
              const roles = [];
              if (p.identity === roomMeta?.host_user_id) roles.push("host");
              if ((roomMeta?.cohost_user_ids || []).includes(p.identity)) roles.push("cohost");
              if (p.identity === currentDjUserId || p.role === "dj") roles.push("dj");
              if (!roles.length) roles.push(p.identity?.startsWith?.("g_") ? "guest" : "listener");
              return {
                identity: p.identity, name: p.name, joined_at: p.joined_at,
                role: roles[0], roles, isMe: p.isLocal,
              };
            })}
            myIdentity={myIdentity}
            moderationRole={isGlobalSuperAdmin ? "host" : appRole}
            onOpenDM={(ident, name) => {
              setChatOpen(true); setParticipantsOpen(false);
              setTimeout(() => chatPanelRef.current?.openDmWith?.(ident, name), 60);
            }}
            onAction={(action, p) => {
              if (action === "make_cohost") handleModerate?.(p.identity, "make_cohost");
              if (action === "revoke_cohost") handleModerate?.(p.identity, "revoke_cohost");
              if (action === "kick") handleModerate?.(p.identity, "kick_to_waiting");
              if (action === "remove") handleModerate?.(p.identity, "remove_until_reset");
              if (action === "make_dj") handleDjAction("assign", p.identity, `${p.name || p.identity} is now the DJ`);
              if (action === "revoke_dj") handleDjAction("assign", roomMeta?.host_user_id, `${p.name || p.identity} is no longer the DJ`);
              if (action === "transfer_host") handleTransferHostRequest(p.identity, p.name);
            }}
          />
        </div>
      )}

      {/* Phase 3.2 — sticky bottom toolbar dock */}
      {phase === "connected" && (
        <BottomDock
          brb={brb}
          onToggleBrb={toggleBrb}
          onOpenDevices={() => setEditingDevices(true)}
          isCurrentDj={isCurrentDj}
          djBoardOpen={djPopoutAlive || viewMode !== "ROOM_ONLY"}
          onOpenDjBoard={() => openDjPopout()}
          shareAudioOn={shareAudioOn}
          onToggleShareAudio={handleToggleShareAudio}
          participantCount={participants.length}
          onOpenParticipants={() => { setParticipantsOpen(true); setChatOpen(false); }}
          chatUnread={chatUnread}
          onOpenChat={() => { setChatOpen(true); setChatMinimized(false); setChatUnread(0); setParticipantsOpen(false); }}
          isStaff={isStaff}
          manageSlot={canEndRoom ? (
            <HostManageDropdown
              locked={!!roomMeta?.locked}
              onToggleLock={handleToggleLock}
              alwaysCameraOn={roomMeta?.always_camera_on !== false}
              onToggleAlwaysCamera={handleToggleAlwaysCamera}
              onBeginClosing={handleBeginClosing}
              onOpenBotsAndRules={isStaff ? () => setRulesEditorOpen(true) : undefined}
              onResetBans={async () => {
                try {
                  await moderate(roomId, { target_user_id: "*", action: "reset_bans" });
                  toast.success("Bans reset — banned users can rejoin.");
                  try { const r = await getRoom(roomId); setRoomMeta(r); } catch { /* */ }
                } catch (err) {
                  toast.error(`Reset bans failed: ${fmtErr(err)}`);
                }
              }}
              onStopAllActivities={async () => {
                try {
                  await moderate(roomId, { target_user_id: "*", action: "stop_all_activities" });
                  toast.success("Activities paused — chat & reactions disabled for all guests.");
                  try { const r = await getRoom(roomId); setRoomMeta(r); } catch { /* */ }
                } catch (err) {
                  toast.error(`Stop all activities failed: ${fmtErr(err)}`);
                }
              }}
              /* Phase A R4 B3 — End Room moved to single contextual button on the
                 bottom dock to avoid duplication. */
            />
          ) : null}
          isHost={canEndRoom}
          onEndRoom={() => setEndModalOpen(true)}
          onLeave={handleLeaveRoom}
          onSelfReact={(emoji) => broadcastTileReact(myIdentity, emoji)}
          micOn={micOn}
          onToggleMic={() => {
            const r = lkRoomRef.current;
            if (r) {
              const next = !micOn;
              r.localParticipant.setMicrophoneEnabled(next);
              setMicOn(next);
            }
          }}
        />
      )}

      {/* Phase A R6 B9 — Share Audio pre-share modal. */}
      <ShareAudioPreModal
        open={shareAudioPreOpen}
        onCancel={() => setShareAudioPreOpen(false)}
        onContinue={() => { setShareAudioPreOpen(false); toggleShareAudio(); }}
      />

      {/* R18-9 — Idle warning banner. Shown when backend emits `idle_warning`
          via the data channel; host gets the Extend button. Mounted near the
          top of the room frame so it never conflicts with the bottom dock. */}
      <IdleBanner
        visible={!!idleBanner && phase === "connected"}
        isHost={appRole === "host"}
        minutesUntilEnd={idleBanner?.minutes_until_end}
        busyExtending={busyExtending}
        onExtend={handleExtendRoom}
        onDismiss={() => setIdleBanner(null)}
      />

      {/* R14-A3 — Host approval tray for bot escalations. Visible only to staff. */}
      <HostApprovalTray
        isStaff={isStaff}
        cards={hostApprovalCards}
        onDecision={async ({ card_id, decision }) => {
          // Optimistic remove
          setHostApprovalCards((prev) => prev.filter((c) => c.card_id !== card_id));
          try {
            const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rooms/${encodeURIComponent(roomId)}/host-approval/${card_id}/${decision}`, {
              method: "POST", credentials: "include",
              headers: { "Content-Type": "application/json" },
            });
            if (!r.ok) {
              const j = await r.json().catch(() => ({}));
              toast.error(j.detail || `${decision} failed`);
            } else {
              if (decision === "allow") toast.success("Action applied");
              else if (decision === "deny") toast.message("Card dismissed");
              else toast.message("Snoozed 5 min");
            }
          } catch (err) {
            console.error("[host-approval] decide failed:", err);
            toast.error(`${decision} failed`);
          }
        }}
      />

      {/* R18-5 — Camera Requests tray (staff). Renders alongside the bot
          escalation tray with a distinct cyan accent so the two surfaces
          don't fight visually. The mount cluster is in the same screen
          region (top-right), but the empty stub renders below the bot
          tray for predictable stacking. */}
      <CameraRequestsTray
        isStaff={isStaff}
        requests={pendingRequests}
        onApprove={handleApproveCameraRequest}
        onDeny={handleDenyCameraRequest}
        liveCameraCount={liveCameraCount}
        liveCameraCap={roomMeta?.max_live_cameras}
      />

      {/* R18-5 — Audience: floating "Request Camera" CTA when canPublish=false
          in Lounge/Club mode. Placed above the bottom dock so it never
          collides with the dock's center cluster. */}
      {audienceCanRequestCam && (
        <div
          className="fixed z-30 bottom-[72px] left-1/2 -translate-x-1/2 flex flex-col items-center gap-1.5"
          data-testid="audience-request-camera-wrap"
        >
          {myPendingRequestId ? (
            <div
              className="px-3.5 py-2 rounded-full bg-amber-500/15 border border-amber-400/35 text-amber-200 text-xs font-bold uppercase tracking-[0.16em] inline-flex items-center gap-2"
              data-testid="audience-request-pending-pill"
            >
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Request pending — host will review
            </div>
          ) : (
            <button
              onClick={handleRequestCamera}
              disabled={requestingCam}
              className="px-4 py-2 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white text-xs font-bold uppercase tracking-[0.16em] hover:opacity-90 disabled:opacity-40 inline-flex items-center gap-2 shadow-[0_10px_28px_rgba(255,0,127,0.35)]"
              data-testid="audience-request-camera-btn"
              title={roomMode === "lounge"
                ? "Ask the host to put you on cam in this Listening space"
                : "Ask the host to put you on cam in this DJ set"}
            >
              {requestingCam ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
              Request Camera
            </button>
          )}
        </div>
      )}

      {/* R14-D — Rules editor side panel. Staff-only. */}
      {rulesEditorOpen && isStaff && (
        <RulesEditor
          roomId={roomId}
          roomMeta={roomMeta}
          botToggles={roomMeta?.bot_toggles || {}}
          onClose={() => setRulesEditorOpen(false)}
        />
      )}

      {/* R15-3b — Bots & Rules now lives inside the Host Manage dropdown
          (HostManageDropdown.onOpenBotsAndRules) for hosts. The floating
          button was removed to reduce dock clutter. Cohosts open it via the
          same dropdown when their token includes manage-bot capability. */}

      {/* R13-D — Edit Devices flip-back. Mounts on top of the live room view.
          The user stays connected to LiveKit; only the rendered preview pane
          is a fresh getUserMedia stream that lets them audition new devices.
          Selecting a new cam triggers `setCameraEnabled(true, {deviceId})` on
          the localParticipant, which under the hood does the replaceTrack
          dance LiveKit recommends — other participants see no black flash. */}
      {editingDevices && (
        <div className="fixed inset-0 z-50" data-testid="room-edit-devices-overlay">
          <PreRoomPreview
            mode="edit"
            roomName={roomMeta?.name || ""}
            onDone={async (payload) => {
              // R15-3d — propagate the rename (if any) before tearing down
              // the edit overlay. The tile + LiveKit canonical name update
              // via the existing setName + setMetadata path used at connect.
              try {
                const r = lkRoomRef.current;
                const newName = (payload?.displayName || "").trim();
                if (r && newName && newName !== r.localParticipant.name) {
                  await setMyRoomDisplayName(roomId, newName);
                  await r.localParticipant.setName(newName);
                  try {
                    const prevMeta = (() => {
                      try { return JSON.parse(r.localParticipant.metadata || "{}"); }
                      catch { return {}; }
                    })();
                    await r.localParticipant.setMetadata(
                      JSON.stringify({ ...prevMeta, display_name: newName })
                    );
                  } catch { /* */ }
                  // eslint-disable-next-line no-console
                  console.log("[R15-3d] in-room rename →", newName);
                  toast.message(`Renamed to "${newName}"`, { duration: 3000 });
                }
              } catch (err) {
                // eslint-disable-next-line no-console
                console.warn("[R15-3d] rename failed:", err);
              }
              setEditingDevices(false);
            }}
            onDeviceChange={async ({ kind, deviceId }) => {
              const r = lkRoomRef.current;
              if (!r) return;
              try {
                if (kind === "camera") {
                  await r.localParticipant.setCameraEnabled(true, { deviceId: { exact: deviceId } });
                  // eslint-disable-next-line no-console
                  console.log(`[devices] camera hot-swapped to ${deviceId}`);
                } else if (kind === "speaker") {
                  // Best-effort: route every <audio>/<video> element to the picked sink.
                  document.querySelectorAll("audio,video").forEach((el) => {
                    if (typeof el.setSinkId === "function") el.setSinkId(deviceId).catch(() => {});
                  });
                  // eslint-disable-next-line no-console
                  console.log(`[devices] audio output hot-swapped to ${deviceId}`);
                }
                // mic is never published in this listener room — preview-only.
              } catch (err) {
                // eslint-disable-next-line no-console
                console.error(`[devices] hot-swap (${kind}) failed:`, err);
                toast.error(`Device swap failed: ${err.message || err}`);
              }
            }}
          />
        </div>
      )}

    </div>
  );
}

/**
 * Phase 3.6 — placeholder shown in the main tab while the DJ board pop-out is open.
 * The engine + LiveKit publish continue to run in this tab; the DJ controls the
 * decks from the pop-out via BroadcastChannel. Clicking "Bring back" attempts
 * to close the named pop-out window; if the user already closed it, the next
 * heartbeat timeout flips popoutAlive false and re-mounts DjConsole2.
 */
function DjBoardPopoutPlaceholder({ onBringBack, onOpenAgain }) {
  return (
    <div className="h-full w-full grid place-items-center px-4" data-testid="dj-board-popout-placeholder">
      <div className="max-w-md w-full sr-glass rounded-2xl p-6 border border-white/10 text-center">
        <div className="w-12 h-12 mx-auto mb-3 rounded-lg bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center">
          <Sliders className="w-5 h-5 text-white" />
        </div>
        <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">DJ Board</div>
        <h3 className="font-display text-lg font-bold mb-2">In a separate window</h3>
        <p className="text-sm text-white/60 mb-4 leading-relaxed">
          The decks are being driven from the pop-out window. The audio engine + LiveKit broadcast keep running here in the main tab.
        </p>
        <div className="flex gap-2 justify-center">
          <button
            onClick={onOpenAgain}
            className="px-3 py-2 text-xs font-bold bg-white/5 hover:bg-white/10 border border-white/10 rounded-md uppercase tracking-[0.12em]"
            data-testid="dj-popout-focus-btn"
          >
            Focus pop-out
          </button>
          <button
            onClick={onBringBack}
            className="px-3 py-2 text-xs font-bold bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-[#0A0A0F] rounded-md uppercase tracking-[0.12em]"
            data-testid="dj-popout-bringback-btn"
          >
            Bring back inline
          </button>
        </div>
      </div>
    </div>
  );
}

function CamTile({ p, canAssignDj, isCurrentDj, currentDjUserId, myIdentity, hostId,
                   isTileHost, isTileCohost, isTileDj, onDjAction, onModAction, onTransferHost,
                   onRename, onChat, djPulse = false, bursts = [], onTileReact,
                   isPinned = false, onTogglePin, pinDisabled = false,
                   tileHasCameraGrant = false, roomMode = "club",
                   onInviteCam, onRevokeCam, subscribeIntent }) {
  const videoRef = useRef(null);
  // R18-7 — Root DOM ref for the IntersectionObserver. The observer toggles
  // remote video subscription for off-screen tiles so we stop downloading
  // bytes for cameras the user can't see — the biggest single egress lever.
  const tileRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [pip, setPip] = useState(null); // {x, y} for right-click PiP menu
  const [reactOpen, setReactOpen] = useState(false);

  // R18-7 — Mirror `isPinned` into a ref so the IntersectionObserver callback
  // ALWAYS reads the live pinned state. Without this the closure would be
  // stale and an off-screen-but-pinned tile could still get unsubscribed when
  // the user pins it AFTER scroll. Also force-subscribe on the prop flipping
  // to `true` so a freshly-pinned off-screen tile re-grabs video immediately.
  const isPinnedRef = useRef(isPinned);
  useEffect(() => {
    isPinnedRef.current = isPinned;
    if (isPinned && subscribeIntent) subscribeIntent(p.identity, "video", true);
  }, [isPinned, p.identity, subscribeIntent]);

  // R18-7 — IntersectionObserver. threshold 0.1 = ≥10% visible counts as
  // "in view". A single shared 750ms debounce timer per tile collapses rapid
  // scroll-in / scroll-out bursts into one subscription flip.
  useEffect(() => {
    if (!subscribeIntent || !tileRef.current) return undefined;
    const identity = p.identity;
    let timer = null;
    const obs = new IntersectionObserver((entries) => {
      // Last entry wins — IO only fires when state actually changes, but
      // chained changes during the debounce window collapse correctly here.
      const e = entries[entries.length - 1];
      const visible = !!e?.isIntersecting;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (visible) {
          subscribeIntent(identity, "video", true);
        } else if (!isPinnedRef.current) {
          // Pinned tiles never unsubscribe even when off-screen.
          subscribeIntent(identity, "video", false);
        }
      }, 750);
    }, { threshold: 0.1 });
    obs.observe(tileRef.current);
    return () => {
      try { obs.disconnect(); } catch { /* noop */ }
      if (timer) { clearTimeout(timer); timer = null; }
      // Cleanup — if this tile is leaving the DOM, it's definitely not
      // visible. Unsubscribe unless it's pinned (a pin gallery re-mount can
      // race the unmount of the same identity in the audience grid).
      if (!isPinnedRef.current) subscribeIntent(identity, "video", false);
    };
  }, [p.identity, subscribeIntent]);

  useEffect(() => {
    const pub = p.videoPublication;
    if (!pub) return;
    const track = pub.track;
    if (track && videoRef.current) {
      const el = videoRef.current;
      track.attach(el);
      return () => { try { track.detach(el); } catch { /* ignore */ } };
    }
  }, [p.videoPublication, p.videoPublication?.track]);

  // Click-outside to close both menus
  useEffect(() => {
    if (!menuOpen && !pip) return;
    const close = (e) => {
      // Don't close while clicking inside our menus
      if (e.target.closest("[data-tile-menu]") || e.target.closest("[data-pip-menu]")) return;
      setMenuOpen(false); setPip(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen, pip]);

  // Phase 2.2.x — IMPORTANT: all gating below derives from roomMeta-backed
  // props (`isTileHost`/`isTileCohost`/`isTileDj`), NEVER from `p.appRole`/
  // `p.role`. Those LiveKit-metadata fields are stamped at token issue time
  // and don't refresh when the host promotes/demotes/transfers; the polling
  // loop in Room.jsx keeps roomMeta fresh within 5s, so the menu always
  // reflects current server state.
  const isSelf = p.identity === myIdentity;
  const meIsHost = myIdentity === hostId;
  const meIsCurrentDj = !!isCurrentDj;
  // Phase 2.2.x policy: DJ slot can be handed to ANY participant in the room,
  // not just staff. Actor must be host, cohost-with-permission, or the current
  // DJ. `canAssignDj` (passed in) already covers host + cohost-with-perm; we
  // OR in `meIsCurrentDj` so a passing DJ can hand off without going through
  // the host.
  const canIAssignDj = canAssignDj || meIsCurrentDj;

  // Compose menu items in a clear, top-down order.
  const items = [];

  if (!isSelf) {
    // ------- Actions a viewer can take ON ANOTHER TILE -------
    // DJ assignment (any role can be the target, gated only by actor permission).
    if (canIAssignDj && !isTileDj) {
      items.push({ key: "make-dj", icon: "🎙️", label: "Make DJ",
        run: () => onDjAction && onDjAction("assign", p.identity, `${p.name} is now the DJ`) });
    }
    // Phase 2.3 — Move to waiting room (host only; cohost also for listeners).
    // Inserted here so the destructive items group near the top of the
    // moderation cluster, before the rename/chat soft actions.
    if (meIsHost && !isTileHost) {
      items.push({ key: "waiting", icon: "🪑", label: "Move to waiting room",
        run: () => onModAction && onModAction(p.identity, "kick_to_waiting") });
    } else if (!meIsHost && canAssignDj && !isTileHost && !isTileCohost && !isTileDj) {
      // Cohost can move a plain listener to the waiting room.
      items.push({ key: "waiting", icon: "🪑", label: "Move to waiting room",
        run: () => onModAction && onModAction(p.identity, "kick_to_waiting") });
    }
    // 💬 Chat — visible to EVERY viewer on EVERY non-self tile (per matrix).
    if (onChat) {
      items.push({ key: "chat", icon: "💬", label: "Chat",
        run: () => onChat(p.identity, p.name) });
    }
    // ✏️ Rename — host can rename any non-host tile; cohost can only rename
    // a plain listener (NOT another cohost / host / current DJ).
    if (onRename) {
      const canRenameAsHost = meIsHost && !isTileHost;
      const canRenameAsCohost = !meIsHost && canAssignDj && !isTileHost && !isTileCohost && !isTileDj;
      if (canRenameAsHost || canRenameAsCohost) {
        items.push({ key: "rename", icon: "✏️", label: "Rename",
          run: () => onRename(p.identity, p.name) });
      }
    }
    // Host-tier moderation chain (Revoke cohost / Make host / Remove).
    if (meIsHost) {
      if (!isTileCohost && !isTileHost) {
        items.push({ key: "cohost", icon: "🛡️", label: "Make cohost",
          run: () => onModAction && onModAction(p.identity, "make_cohost") });
      }
      if (isTileCohost) {
        items.push({ key: "revoke", icon: "🛡️", label: "Revoke cohost",
          run: () => onModAction && onModAction(p.identity, "revoke_cohost") });
        items.push({ key: "make-host", icon: "👑", label: "Make host",
          run: () => onTransferHost && onTransferHost(p.identity, p.name) });
      }
      if (!isTileHost) {
        items.push({ key: "remove", icon: "🚫", label: "Remove until reset",
          run: () => onModAction && onModAction(p.identity, "remove_until_reset") });
      }
    }
    // R18-5 — Per-tile camera invite / revoke for staff in Lounge/Club mode.
    // Host can act on anyone; cohost cannot act on host (mirrors existing
    // moderation matrix).
    const camMgmtAllowed = (meIsHost || (!meIsHost && canIAssignDj))
      && !isTileHost
      && (roomMode === "lounge" || roomMode === "club");
    if (camMgmtAllowed) {
      if (tileHasCameraGrant) {
        items.push({ key: "revoke-cam", icon: "📷", label: "Revoke camera",
          run: () => onRevokeCam && onRevokeCam(p.identity, p.name) });
      } else {
        items.push({ key: "invite-cam", icon: "📷", label: "Invite to cam",
          run: () => onInviteCam && onInviteCam(p.identity, p.name) });
      }
    }
  } else {
    // ------- Actions a viewer can take ON THEIR OWN TILE -------
    // Step down is for a non-host DJ; for the host, step_down is a no-op (it
    // would resolve new_dj=host=cur_dj → unchanged), so we hide it entirely.
    if (meIsCurrentDj && !meIsHost) {
      items.push({ key: "step", icon: "🎤", label: "Step down",
        run: () => onDjAction && onDjAction("step_down", null, "You're back as participant") });
    }
    if (meIsHost && !meIsCurrentDj) {
      items.push({ key: "claim", icon: "👑", label: "Take DJ back",
        run: () => onDjAction && onDjAction("claim", null, "You're the DJ again") });
    }
  }

  // Diagnostics — log every time the menu opens, so the user can screenshot
  // the DevTools console for triage without having to know what to look for.
  // (The diagnostics pill stays the primary "no DevTools" channel; this is
  // belt-and-suspenders for the dev who DOES open DevTools.)
  if (menuOpen) {
    // eslint-disable-next-line no-console
    console.log("[ITEMS]", {
      tile: String(p.identity).slice(0, 12), name: p.name,
      meIsHost, meIsCurrentDj, isSelf,
      isTileHost, isTileCohost, isTileDj,
      canIAssignDj,
      itemKeys: items.map((i) => i.key),
    });
  }

  const onVideoContext = (e) => {
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setPip({ x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  const goPiP = async () => {
    try { await videoRef.current?.requestPictureInPicture(); }
    catch (err) { console.warn("PiP failed:", err); }
    setPip(null);
  };

  return (
    <div
      ref={tileRef}
      className={`relative aspect-video rounded-lg overflow-hidden border transition-colors group ${
        isTileDj
          ? "border-[#FF007F]/60 shadow-[0_0_24px_rgba(255,0,127,0.28)] ring-1 ring-[#FF007F]/40"
          : isTileCohost
            ? "border-[#00F0FF]/40"
            : "border-white/10 hover:border-white/20"
      } bg-[#0A0A0F] ${djPulse ? "sr-dj-pulse" : ""}`}
      data-testid={`cam-tile-${p.identity}`}
      data-brb={p.brb ? "true" : "false"}
    >
      {/* Phase 3 Stage 2 — emoji bursts overlay */}
      {bursts.length > 0 && (
        <div className="pointer-events-none absolute inset-0 z-30 overflow-hidden" data-testid={`tile-bursts-${p.identity}`}>
          {bursts.map((b) => (
            <div key={b.id}
                 className="sr-tile-burst absolute bottom-2 left-1/2 -translate-x-1/2 text-3xl select-none"
                 style={{ "--drift": `${b.drift}px` }}
                 data-testid={`tile-burst-${b.id}`}>
              <span>{b.emoji}</span>
              {b.count > 1 && <span className="ml-0.5 text-[10px] align-top text-white font-bold drop-shadow-[0_1px_3px_rgba(255,0,127,0.9)]">× {b.count}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Phase 3 Stage 2 — per-tile emoji react button (lower-left, mobile-friendly tap target) */}
      {onTileReact && (
        <div className="absolute bottom-1.5 left-1.5 z-30 flex gap-1.5">
          <button
            onClick={(e) => { e.stopPropagation(); setReactOpen((v) => !v); }}
            className="w-9 h-9 grid place-items-center rounded-full bg-black/60 hover:bg-black/85 border border-white/15 text-white opacity-0 group-hover:opacity-100 focus:opacity-100 touch-manipulation transition-opacity"
            data-testid={`tile-react-btn-${p.identity}`}
            aria-label="Send reaction"
            style={{ minWidth: 36, minHeight: 36 }}
          >
            <Smile className="w-4 h-4" />
          </button>
          {/* R16-D — Pin/unpin toggle, local-only (no broadcast). Disabled when
              max-pinned (9) is already reached on this viewer. */}
          {onTogglePin && (
            <button
              onClick={(e) => { e.stopPropagation(); if (!pinDisabled || isPinned) onTogglePin(); }}
              className={`w-9 h-9 grid place-items-center rounded-full border touch-manipulation transition-opacity ${
                isPinned
                  ? "bg-amber-500/85 hover:bg-amber-500 border-amber-300 text-[#0A0A0F] opacity-100"
                  : pinDisabled
                    ? "bg-black/40 border-white/10 text-white/30 cursor-not-allowed opacity-0 group-hover:opacity-100"
                    : "bg-black/60 hover:bg-black/85 border-white/15 text-white opacity-0 group-hover:opacity-100 focus:opacity-100"
              }`}
              data-testid={`tile-pin-btn-${p.identity}`}
              aria-label={isPinned ? "Unpin tile" : "Pin tile"}
              title={isPinned ? "Unpin (local)" : pinDisabled ? "Max 15 pins" : "Pin (local)"}
              style={{ minWidth: 36, minHeight: 36 }}
              disabled={pinDisabled && !isPinned}
            >
              {isPinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
            </button>
          )}
          {reactOpen && (
            <TileReactPicker
              onPick={(em) => { onTileReact(em); setReactOpen(false); }}
              onClose={() => setReactOpen(false)}
            />
          )}
        </div>
      )}

      {/* Always-visible ⋯ menu (top-right). Only renders if there's at least one action. */}
      {items.length > 0 && (
        <div className="absolute top-1 right-1 z-30" data-tile-menu>
          <button
            onClick={(e) => { e.stopPropagation(); setMenuOpen((s) => !s); }}
            className="w-9 h-9 grid place-items-center rounded-md bg-black/60 hover:bg-black/80 border border-white/20 text-white touch-manipulation"
            data-testid={`tile-menu-btn-${p.identity}`}
            aria-label="Tile actions"
            style={{ minWidth: 36, minHeight: 36 }}
          >
            <MoreVertical className="w-4 h-4" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 min-w-[170px] bg-black/95 backdrop-blur border border-white/15 rounded-lg shadow-2xl py-1 z-40" data-testid={`tile-menu-${p.identity}`}>
              {items.map((it) => (
                <button
                  key={it.key}
                  onClick={(e) => { e.stopPropagation(); setMenuOpen(false); it.run(); }}
                  className="w-full text-left px-2.5 py-1.5 text-[13px] text-white hover:bg-white/10 flex items-center gap-2"
                  data-testid={`tile-menu-${it.key}-${p.identity}`}
                >
                  <span className="text-[14px]">{it.icon}</span>{it.label}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Custom right-click → PiP-only menu, anchored at cursor */}
      {pip && (
        <div
          className="absolute z-30 bg-black/95 backdrop-blur border border-white/15 rounded-lg shadow-2xl py-1 min-w-[180px]"
          style={{ left: Math.min(pip.x, 200), top: Math.min(pip.y, 120) }}
          data-pip-menu
          data-testid={`pip-menu-${p.identity}`}
        >
          <button
            onClick={(e) => { e.stopPropagation(); goPiP(); }}
            className="w-full text-left px-3 py-2 text-sm text-white hover:bg-white/10 flex items-center gap-2"
            data-testid={`pip-btn-${p.identity}`}
          >
            <PictureInPicture2 className="w-4 h-4 text-[#00F0FF]" />Picture in Picture
          </button>
        </div>
      )}
      {isTileDj && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white text-[10px] font-bold uppercase tracking-[0.18em] shadow-[0_4px_16px_-2px_rgba(255,0,127,0.55)]" data-testid={`dj-badge-${p.identity}`}>
          <Crown className="w-3 h-3" strokeWidth={2.5} />DJ
        </div>
      )}
      {isTileCohost && !isTileDj && (
        <div className="absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#00F0FF]/20 border border-[#00F0FF]/40 text-[#00F0FF] text-[10px] font-bold uppercase tracking-[0.18em]" data-testid={`cohost-badge-${p.identity}`}>
          <Shield className="w-3 h-3" strokeWidth={2.5} />Cohost
        </div>
      )}

      {p.videoPublication && p.videoPublication.track ? (
        <video ref={videoRef} autoPlay playsInline muted={p.isLocal}
          className="w-full h-full object-cover"
          onContextMenu={onVideoContext}
          controlsList="nodownload nofullscreen noremoteplayback"
          disablePictureInPicture={false}
        />
      ) : (
        <div className="absolute inset-0 grid place-items-center">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/5 border border-white/10 grid place-items-center mx-auto mb-2 overflow-hidden">
              {p.avatarUrl ? (
                <img src={p.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-display text-base font-bold text-white/60">{(p.name || "?").slice(0, 2).toUpperCase()}</span>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{isTileDj ? "DJ · cam off" : "no cam"}</div>
          </div>
        </div>
      )}

      <div className="absolute bottom-2 left-2 z-10 flex items-center gap-1.5 bg-black/60 backdrop-blur-md rounded-md px-2 py-1">
        {isTileDj && <Disc3 className="w-3 h-3 text-[#FF007F]" />}
        <span className="text-xs font-semibold text-white truncate max-w-[140px]">
          {p.name}
          {p.isLocal && <span className="ml-1.5 text-[9px] uppercase tracking-wider text-white/40">you</span>}
        </span>
      </div>

      {p.brb && (
        <div className="absolute inset-0 z-20 grid place-items-center bg-black/40 backdrop-blur-sm" data-testid={`brb-overlay-${p.identity}`}>
          <div className="sr-brb bg-[#FF007F] text-white px-5 py-2 rounded-full text-sm font-bold uppercase tracking-[0.2em] shadow-[0_0_28px_rgba(255,0,127,0.6)]" data-testid={`brb-badge-${p.identity}`}>
            BRB
          </div>
        </div>
      )}
    </div>
  );
}

function DjConsole({ fileName, playing, onPick, onPlay, onPause, onStop }) {
  const fileInputRef = useRef(null);
  return (
    <div className="fixed bottom-0 inset-x-0 z-30 px-4 sm:px-6 lg:px-8 pb-4" data-testid="dj-console">
      <div className="max-w-[1600px] mx-auto sr-glass rounded-2xl p-3 sm:p-4 flex flex-wrap items-center gap-3 sm:gap-4 shadow-[0_-10px_40px_-10px_rgba(0,0,0,0.6)]">
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center shrink-0">
            <Disc3 className="w-5 h-5 text-white" style={playing ? { animation: "spin 4s linear infinite" } : {}} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">DJ Deck</div>
            <div className="font-medium text-sm truncate max-w-[60vw] sm:max-w-md" data-testid="dj-current-track">{fileName || "No file loaded"}</div>
          </div>
        </div>
        <input ref={fileInputRef} type="file" accept="audio/*" onChange={onPick} className="hidden" data-testid="dj-file-input" />
        <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg text-sm font-semibold" data-testid="dj-pick-file-btn">
          <Upload className="w-4 h-4" />Load track
        </button>
        <div className="flex items-center gap-2">
          {playing ? (
            <button onClick={onPause} className="flex items-center gap-2 px-5 py-2.5 bg-white text-black font-bold rounded-lg hover:bg-white/90" data-testid="dj-pause-btn">
              <Pause className="w-4 h-4" />Pause
            </button>
          ) : (
            <button onClick={onPlay} disabled={!fileName} className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-bold rounded-lg hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed" data-testid="dj-play-btn">
              <Play className="w-4 h-4" />Play
            </button>
          )}
          <button onClick={onStop} disabled={!fileName} className="p-2.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 disabled:opacity-30 disabled:cursor-not-allowed" data-testid="dj-stop-btn" aria-label="Stop">
            <Square className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function DiagPill({ myIdentity, currentDjUserId, hostId, cohostCount, recentDataMsgs, expanded, onToggle }) {
  const short = (s) => (s ? String(s).slice(0, 10) : "—");
  const last = recentDataMsgs && recentDataMsgs.length > 0 ? recentDataMsgs[0] : null;
  // Highlight: red if me-vs-dj mismatch is what the user is debugging
  return (
    <div className="relative" data-testid="diag-pill-wrap">
      <button
        onClick={onToggle}
        className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 font-mono text-[10px] tracking-tight transition-colors max-w-[420px] truncate"
        data-testid="diag-pill"
        title="Click to expand · live state for debugging">
        <span className="text-white/50">👤</span>
        <span data-testid="diag-me" className="text-white/90">{short(myIdentity)}</span>
        <span className="text-white/20">·</span>
        <span className="text-[#FF007F]/90">🎤</span>
        <span data-testid="diag-dj" className={`${myIdentity && myIdentity === currentDjUserId ? "text-[#00F0FF] font-bold" : "text-white/90"}`}>{short(currentDjUserId)}</span>
        <span className="text-white/20">·</span>
        <span className="text-amber-300/90">👑</span>
        <span data-testid="diag-host" className="text-white/90">{short(hostId)}</span>
        <span className="text-white/20">·</span>
        <span className="text-white/50">🛡️</span>
        <span data-testid="diag-cohosts" className="text-white/90">{cohostCount}</span>
        {last && (
          <>
            <span className="text-white/20">·</span>
            <span className="text-white/50">rx</span>
            <span data-testid="diag-rx" className="text-[#00F0FF]/90">{last.type}@{last.at}</span>
          </>
        )}
      </button>
      {expanded && (
        <div className="absolute right-0 top-[calc(100%+6px)] z-40 w-[420px] max-w-[92vw] rounded-lg bg-[#0A0A0F] border border-white/15 shadow-2xl p-3 font-mono text-[11px] leading-relaxed" data-testid="diag-pill-popover">
          <div className="text-white/40 text-[9px] uppercase tracking-[0.2em] mb-2">live state</div>
          <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 mb-3">
            <div className="text-white/40">me</div><div className="text-white break-all" data-testid="diag-full-me">{myIdentity || "—"}</div>
            <div className="text-white/40">dj</div><div className="text-white break-all" data-testid="diag-full-dj">{currentDjUserId || "—"}</div>
            <div className="text-white/40">host</div><div className="text-white break-all" data-testid="diag-full-host">{hostId || "—"}</div>
            <div className="text-white/40">cohosts</div><div className="text-white">{cohostCount}</div>
            <div className="text-white/40">isDj?</div>
            <div className={myIdentity && myIdentity === currentDjUserId ? "text-[#00F0FF]" : "text-white/60"}>
              {myIdentity && myIdentity === currentDjUserId ? "YES (console should be mounted)" : "no"}
            </div>
          </div>
          <div className="text-white/40 text-[9px] uppercase tracking-[0.2em] mb-1">last 5 data msgs</div>
          {(recentDataMsgs || []).length === 0 ? (
            <div className="text-white/40 italic" data-testid="diag-no-msgs">no data messages received yet</div>
          ) : (
            <ul className="space-y-0.5" data-testid="diag-msg-list">
              {recentDataMsgs.map((m, i) => (
                <li key={i} className="text-white/80">
                  <span className="text-white/40">{m.at}</span>
                  <span className="mx-1.5 text-white/30">·</span>
                  <span className="text-[#00F0FF]">{m.type}</span>
                  <span className="mx-1.5 text-white/30">from</span>
                  <span className="text-white/60">{String(m.from).slice(0, 12)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ModerationDrawer({ room, appRole, onClose, onAction, onEndRoom, participants, currentUserId }) {
  const cohostIds = new Set(room.cohost_user_ids || []);
  const bannedIds = new Set(room.banned_until_reset || []);
  const waiting = room.waiting_room || [];

  // Build view list: from current LK participants (live in room)
  const live = participants.filter((p) => p.identity !== room.host_user_id);

  return (
    <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-96 z-50 sr-glass border-l border-white/10 overflow-y-auto" data-testid="moderation-drawer">
      <div className="p-5 border-b border-white/10 flex items-center justify-between sticky top-0 sr-glass">
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-[#00F0FF]" />
          <h2 className="font-display font-bold">Moderation</h2>
        </div>
        <button onClick={onClose} className="p-1.5 rounded-full hover:bg-white/10" data-testid="moderation-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-5 space-y-6">
        {appRole === "host" && onEndRoom && (
          <button onClick={onEndRoom} className="w-full bg-[#FF007F]/15 hover:bg-[#FF007F]/25 border border-[#FF007F]/40 text-[#FF007F] font-bold rounded-lg px-4 py-2.5 text-sm" data-testid="end-room-btn">
            Close this event for everyone
          </button>
        )}

        <section>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">In the room ({live.length})</div>
          {live.length === 0 ? <div className="text-xs text-white/40">Only you so far.</div> : (
            <ul className="space-y-2">
              {live.map((p) => {
                const isCohost = cohostIds.has(p.identity);
                return (
                  <li key={p.identity} className="sr-glass-soft rounded-lg p-3" data-testid={`mod-row-${p.identity}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {p.name}
                          {isCohost && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-[#00F0FF]">cohost</span>}
                        </div>
                        <div className="text-[10px] text-white/30 font-mono truncate">{p.identity}</div>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {appRole === "host" && !isCohost && (
                        <ModBtn onClick={() => onAction(p.identity, "make_cohost")} testid={`mod-make-cohost-${p.identity}`}>
                          <UserPlus className="w-3 h-3" /> Make cohost
                        </ModBtn>
                      )}
                      {appRole === "host" && isCohost && (
                        <ModBtn onClick={() => onAction(p.identity, "revoke_cohost")} testid={`mod-revoke-cohost-${p.identity}`}>
                          <UserMinus className="w-3 h-3" /> Revoke cohost
                        </ModBtn>
                      )}
                      <ModBtn onClick={() => onAction(p.identity, "kick_to_waiting")} testid={`mod-kick-${p.identity}`}>
                        <Coffee className="w-3 h-3" /> Kick to waiting
                      </ModBtn>
                      <ModBtn variant="danger" onClick={() => onAction(p.identity, "remove_until_reset")} testid={`mod-remove-${p.identity}`}>
                        <Ban className="w-3 h-3" /> Remove
                      </ModBtn>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        <section>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2">Waiting room ({waiting.length})</div>
          {waiting.length === 0 ? <div className="text-xs text-white/40">No one waiting.</div> : (
            <ul className="space-y-2">
              {waiting.map((w) => (
                <li key={w.user_id} className="sr-glass-soft rounded-lg p-3 flex items-center justify-between gap-2" data-testid={`mod-wait-row-${w.user_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{w.display_name || "Participant"}</div>
                    <div className="text-[10px] text-white/30 font-mono truncate">{w.user_id}</div>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <ModBtn onClick={() => onAction(w.user_id, "approve_from_waiting")} testid={`mod-approve-${w.user_id}`}>
                      <UserCheck className="w-3 h-3" /> Admit
                    </ModBtn>
                    <ModBtn onClick={() => onAction(w.user_id, "remove_until_reset")} variant="danger" testid={`mod-wait-remove-${w.user_id}`}>
                      <Ban className="w-3 h-3" /> Remove
                    </ModBtn>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Removed ({bannedIds.size})</div>
            {appRole === "host" && bannedIds.size > 0 && (
              <button onClick={() => onAction("*", "reset_bans")} className="flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#00F0FF] hover:text-white" data-testid="mod-reset-bans-btn">
                <RotateCcw className="w-3 h-3" /> Reset
              </button>
            )}
          </div>
          {bannedIds.size === 0 ? <div className="text-xs text-white/40">No one removed.</div> : (
            <ul className="space-y-1.5">
              {[...bannedIds].map((id) => (
                <li key={id} className="text-xs text-white/50 font-mono truncate" data-testid={`mod-banned-${id}`}>{id}</li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </aside>
  );
}

function ModBtn({ children, onClick, variant, testid }) {
  const base = "flex items-center gap-1 px-2.5 py-1 rounded-md text-[11px] font-semibold transition-colors";
  const styles = variant === "danger"
    ? "bg-[#FF007F]/10 hover:bg-[#FF007F]/20 text-[#FF007F] border border-[#FF007F]/30"
    : "bg-white/5 hover:bg-white/10 text-white border border-white/10";
  return <button onClick={onClick} className={`${base} ${styles}`} data-testid={testid}>{children}</button>;
}

function Notice({ icon, title, message, onBack, backLabel = "Back to home", testid }) {
  return (
    <div className="min-h-screen sr-aurora grid place-items-center px-6" data-testid={testid}>
      <div className="sr-glass rounded-2xl p-8 max-w-md text-center">
        <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-white/5 border border-white/10 grid place-items-center">{icon}</div>
        <h2 className="font-display text-2xl font-bold mb-2">{title}</h2>
        <p className="text-white/60 text-sm mb-6">{message}</p>
        <button onClick={onBack} className="px-6 py-3 bg-gradient-to-r from-[#FF007F] to-[#00F0FF] text-white font-bold rounded-lg hover:opacity-90" data-testid="notice-back-btn">{backLabel}</button>
      </div>
    </div>
  );
}


